import { previewPdf } from './PdfPreview';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Download, Loader2, X, PenLine, Keyboard,
  ArrowLeft, ArrowRight, Sparkles, RefreshCw,
} from 'lucide-react';
import LatePaymentsDisputeModal from './LatePaymentsDisputeModal';
import BusinessInfoDialog from './BusinessInfoDialog';
import { getLetterStyle, saveLetterStyle } from '../lib/letterStyle';
import { CUSTOM_PICK, pickFor, customLetterFor, downloadCustomLetterPdf, normName } from '../lib/letterPicks';

const API = process.env.REACT_APP_BACKEND_URL;

export const SIG_FONTS = [
  {
    value: 'cursive_elegant',
    label: 'Elegant',
    subLabel: 'Great Vibes',
    preview: '"Great Vibes", "Snell Roundhand", "Apple Chancery", cursive',
  },
  {
    value: 'cursive_handwritten',
    label: 'Handwritten',
    subLabel: 'Dancing Script',
    preview: '"Dancing Script", "Bradley Hand", "Segoe Script", cursive',
  },
  {
    value: 'cursive_casual',
    label: 'Casual',
    subLabel: 'Sacramento',
    preview: '"Sacramento", "Segoe Script", "Bradley Hand", cursive',
  },
  {
    value: 'cursive_allura',
    label: 'Formal Script',
    subLabel: 'Allura',
    preview: '"Allura", "Great Vibes", "Snell Roundhand", cursive',
  },
  {
    value: 'cursive_mrs_saint',
    label: 'Delicate',
    subLabel: 'Mrs Saint Delafield',
    preview: '"Mrs Saint Delafield", "Great Vibes", "Apple Chancery", cursive',
  },
  {
    value: 'cursive_cookie',
    label: 'Playful',
    subLabel: 'Cookie',
    preview: '"Cookie", "Sacramento", "Bradley Hand", cursive',
  },
];

// All handwriting body styles exposed by /api/handwriting-converter — kept
// in sync with backend STYLES map so the dispute letter modal offers every
// available handwriting choice.  Rendered as visual preview cards (label
// shown in the actual font — same UX as the Handwritten Letter Converter).
// Nov 2026 — user requested the sloppy fonts be removed from the picker.
// Retired styles alias to "caveat" on the backend so any legacy saved
// letter still opens.  Public deck below stays LEGIBLE only.
// Feb 2026 — "realistic" (multi-font blend) fully retired at user
// request: "really ugly, remove it from every section it's in."
// Backend now aliases any lingering "realistic" reference to Caveat,
// so old saved letters still render — it just no longer appears as a
// picker option in ANY tool (Dispute Letter Modal or Handwriting
// Converter Modal, which pulls from /api/handwriting/styles).
export const HW_STYLES = [
  // Jun 2026 — scanner-friendly (e-OSCAR) styles first: clear, separate letterforms.
  { value: 'playpen_sans',         label: 'Clear print',          subLabel: 'Playpen Sans · best for scanning', preview: '"Playpen Sans", cursive' },
  { value: 'pangolin',             label: 'Bold clear print',     subLabel: 'Pangolin',                 preview: '"Pangolin", cursive' },
  { value: 'itim',                 label: 'Rounded clear print',  subLabel: 'Itim',                     preview: '"Itim", cursive' },
  { value: 'edu_beginner',         label: 'School print',         subLabel: 'Edu SA Beginner',          preview: '"Edu SA Beginner", cursive' },
  { value: 'mynerve',              label: 'Pen print',            subLabel: 'Mynerve',                  preview: '"Mynerve", cursive' },
  { value: 'comic_neue',           label: 'Easy-read print',      subLabel: 'Comic Neue',               preview: '"Comic Neue", cursive' },
  { value: 'merienda',             label: 'Clear cursive',        subLabel: 'Merienda',                 preview: '"Merienda", cursive' },
  { value: 'courgette',            label: 'Bold clear cursive',   subLabel: 'Courgette',                preview: '"Courgette", cursive' },
  { value: 'playwrite_us',         label: 'Upright school cursive', subLabel: 'Playwrite US Modern',    preview: '"Playwrite US Modern", cursive' },
  { value: 'sriracha',             label: 'Slanted pen cursive',  subLabel: 'Sriracha',                 preview: '"Sriracha", cursive' },
  { value: 'caveat',               label: 'Neat cursive',         subLabel: 'Caveat',                   preview: '"Caveat", cursive' },
  { value: 'kalam',                label: 'Casual print',         subLabel: 'Kalam',                    preview: '"Kalam", cursive' },
  { value: 'patrick_hand',         label: 'Friendly print',       subLabel: 'Patrick Hand',             preview: '"Patrick Hand", cursive' },
  { value: 'architects_daughter',  label: "Architect's block",    subLabel: 'Architects Daughter',      preview: '"Architects Daughter", cursive' },
  { value: 'shadows_into_light',   label: 'Thin marker',          subLabel: 'Shadows Into Light',       preview: '"Shadows Into Light", cursive' },
  { value: 'gloria_hallelujah',    label: 'Bouncy print',         subLabel: 'Gloria Hallelujah',        preview: '"Gloria Hallelujah", cursive' },
  { value: 'rock_salt',            label: 'Bold marker',          subLabel: 'Rock Salt',                preview: '"Rock Salt", cursive' },
  { value: 'coming_soon',          label: 'Soft block',           subLabel: 'Coming Soon',              preview: '"Coming Soon", cursive' },
  { value: 'schoolbell',           label: 'Classroom',            subLabel: 'Schoolbell',               preview: '"Schoolbell", cursive' },
  { value: 'reenie_beanie',        label: 'Loose slant',          subLabel: 'Reenie Beanie',            preview: '"Reenie Beanie", cursive' },
  { value: 'cedarville_cursive',   label: 'Simple cursive',       subLabel: 'Cedarville Cursive',       preview: '"Cedarville Cursive", cursive' },
  { value: 'permanent_marker',     label: 'Sharpie marker',       subLabel: 'Permanent Marker',         preview: '"Permanent Marker", cursive' },
  { value: 'handlee',              label: 'Everyday hand',        subLabel: 'Handlee',                  preview: '"Handlee", cursive' },
  // Nov 2026 — 6 new NEAT handwritten fonts.
  { value: 'delius',               label: 'Neat rounded',         subLabel: 'Delius',                   preview: '"Delius", cursive' },
  { value: 'neucha',               label: 'Marker note',          subLabel: 'Neucha',                   preview: '"Neucha", cursive' },
  { value: 'nanum_pen',            label: 'Ballpoint pen',        subLabel: 'Nanum Pen Script',         preview: '"Nanum Pen Script", cursive' },
  { value: 'beth_ellen',           label: 'Refined script',       subLabel: 'Beth Ellen',               preview: '"Beth Ellen", cursive' },
  { value: 'marck_script',         label: 'Elegant script',       subLabel: 'Marck Script',             preview: '"Marck Script", cursive' },
  { value: 'caveat_brush',         label: 'Bold brush pen',       subLabel: 'Caveat Brush',             preview: '"Caveat Brush", cursive' },
];

// Typed-body font options — for the "Computer / Typed" mode.  Keeps the
// letter uniquely styled across clients even in typed form (extra e-OSCAR
// camouflage on top of the LLM rewording).
const BODY_FONTS = [
  { value: 'arial',      label: 'Arial',           subLabel: 'Clean sans-serif · best for scanning', preview: 'Arial, "Liberation Sans", Helvetica, sans-serif' },
  { value: 'atkinson',   label: 'Atkinson Hyperlegible', subLabel: 'Sans-serif built so letters never look alike', preview: '"Atkinson Hyperlegible", Arial, sans-serif' },
  { value: 'times',      label: 'Times',           subLabel: 'Traditional serif',  preview: '"Times New Roman", "Liberation Serif", Times, serif' },
  { value: 'georgia',    label: 'Georgia',         subLabel: 'Modern serif',       preview: 'Georgia, "Liberation Serif", serif' },
  { value: 'garamond',   label: 'Garamond',        subLabel: 'Classic serif',      preview: '"EB Garamond", Garamond, "Liberation Serif", serif' },
  { value: 'helvetica',  label: 'Helvetica',       subLabel: 'Clean sans-serif',   preview: 'Helvetica, Arial, sans-serif' },
  { value: 'courier',    label: 'Courier',         subLabel: 'Typewriter',         preview: '"Courier New", "Liberation Mono", monospace' },
];

const LETTER_TYPES = [
  {
    value: 'regular',
    label: 'Regular Dispute Letter',
    tag: 'First Round · Aggressive FCRA/CFPB',
    desc: 'Formal first-round demand citing FCRA §1681, FDCPA §1692, CFPB. 7-day deletion window.',
    enabled: true,
  },
  {
    value: 'second_round',
    label: 'Second Round Verified Account Letter',
    tag: 'Follow-Up · Willful Non-Compliance',
    desc: 'Escalation letter for accounts the bureau claimed were "verified" without producing method-of-verification proof.',
    enabled: true,
  },
  {
    // 2026-02-28 (user request) — Third-round FINAL demand for accounts
    // that have already been "verified as accurate" twice.  Maximum
    // aggression: threatens civil litigation, CFPB / FTC / State AG
    // referral.  LLM personalizes per client so every third-round
    // letter reads differently.  Placed right after Second Round so the
    // three "rounds" (Regular → Second → Third) sit together.
    value: 'third_round',
    label: 'Third Round — Final Demand (Legal Action)',
    tag: 'FCRA §616 / §617 / §1681n · MAX Aggression',
    desc: 'Final written notice after two failed "verified as accurate" responses. Demands permanent deletion within 15 days or civil suit will be filed, with copies to CFPB, FTC, and State Attorney General. Every letter uniquely reworded per client.',
    enabled: true,
  },
  {
    // Feb 2026 — user's INQ NEW template. Removes UNAUTHORIZED credit
    // inquiries under FCRA §604 / §616. Accounts on this letter are
    // rendered under "Unauthorized Inquiries to Be Removed:" with the
    // FULL MM-DD-YYYY inquiry date and no balance column.
    value: 'inquiry_removal',
    label: 'Inquiry Removal Letter',
    tag: 'Identity Theft · FCRA §604 & §616',
    desc: 'Demands immediate removal of unauthorized hard inquiries caused by identity theft.  Cites FCRA §604 (permissible purpose) and §616 (civil liability).',
    enabled: true,
  },
  {
    // Feb 2026 — ChexSystems identity-theft dispute (banking-report
    // deletion demand).  Addressed to ChexSystems, Inc., Attn: Consumer
    // Relations, 7805 Hudson Road, Suite 100, Woodbury, MN 55125.
    // Bureau selection is auto-forced to `chexsystems` when this
    // letter type is picked, and the LLM re-drafts the body uniquely
    // per client per generation using the user-supplied wording.
    value: 'chexsystems',
    label: 'ChexSystems Dispute Letter',
    tag: 'Banking-Report Identity Theft',
    desc: 'Formal identity-theft dispute demanding immediate deletion of fraudulent entries from the client\'s ChexSystems consumer report.  Auto-addressed to ChexSystems, Woodbury MN.',
    enabled: true,
  },
  {
    value: 'business',
    label: 'Business Account Removal Letter',
    tag: 'Business Credit (Equifax · Experian · D&B)',
    desc: 'Demand for deletion of inaccurate / unauthorized business credit accounts. Auto-addressed to Equifax Commercial, Experian Business and Dun & Bradstreet. Asks for the business details first.',
    enabled: true,
  },
  {
    // 2026-02-08 (user request) — Bankruptcy verification demand. Argues
    // the bureau is falsely naming the U.S. Bankruptcy Court as the
    // furnisher and cites 15 U.S.C. §1681e(b) & §1681i. LLM personalizes
    // the wording per client per generation while keeping the FCRA
    // arguments intact.
    value: 'bankruptcy',
    label: 'Bankruptcy Dispute Letter',
    tag: 'Public Record · FCRA §1681e(b) & §1681i',
    desc: "Disputes a bankruptcy entry the bureau is claiming was furnished by the U.S. Bankruptcy Court. Demands proof the court furnished the info, method of verification, and immediate deletion if unverifiable.",
    enabled: true,
  },
  {
    // 2026-02-24 (user request) — Late Payments dispute (Fair Credit
    // Billing Act §1666).  Frames the reported late payments as a
    // billing-error notification so the furnisher is required to
    // investigate and correct.  LLM personalizes the wording per client.
    value: 'late_payments',
    label: 'Late Payments Dispute Letter',
    tag: 'Billing Error · FCBA 15 U.S.C. §1666',
    desc: 'Formal billing-error dispute of inaccurate late-payment notations. Cites the Fair Credit Billing Act §1666 and demands the furnisher investigate + delete unverified delinquency reporting.',
    enabled: true,
  },
  {
    // 2026-02-24 (user request) — Child Support reporting dispute.
    // Demands a reasonable investigation (not automated verification) and
    // competent documentary evidence proving legal authority to report.
    // Cites 15 U.S.C. §§1681b, 1681e(b), 1681i, 1681s-2(b) and the
    // Kevin D. Miller v. Trans Union case.
    value: 'child_support',
    label: 'Child Support Dispute Letter',
    tag: 'Public Record · FCRA §1681b / §1681e(b) / §1681i',
    desc: 'Formal FCRA dispute of a child-support account being reported to the bureaus. Demands documentary proof of legal authority, verification records, and permissible purpose — no automated verification accepted.',
    enabled: true,
  },
  {
    // 2026-02-28 (user request) — Identity-theft block for accounts
    // reported as "settled" or "paid in settlement" the client never
    // negotiated.  FCRA §605B / 15 U.S.C. §1681c-2 block.  Every
    // generation is LLM-rewritten so no two clients get identical text.
    value: 'identity_theft_settled',
    label: 'Identity Theft — Fraudulent Settled Accounts',
    tag: 'FCRA §605B · Identity Theft Block',
    desc: 'Demands an immediate identity-theft block on accounts reported as "settled" or "paid in settlement" that the client never negotiated, authorized, or funded. Every letter is uniquely reworded per client so e-OSCAR cannot pattern-match.',
    enabled: true,
  },
  {
    // 2026-02-28 (user request) — Identity-theft block for fraudulent
    // accounts flagged "included in bankruptcy" tied to a bankruptcy
    // the client never filed.  Blocks the accounts AND the false
    // bankruptcy remark.  LLM personalizes wording per client.
    value: 'identity_theft_bankruptcy',
    label: 'Identity Theft — Fraudulent Bankruptcy Accounts',
    tag: 'FCRA §605B · False Bankruptcy Block',
    desc: 'Blocks fraudulent accounts marked "included in bankruptcy" tied to a bankruptcy petition the client never filed. Demands removal of both the bankruptcy remark and the underlying tradeline. Uniquely reworded per client.',
    enabled: true,
  },
  {
    // 2026-02-28 (user request) — Timeshare-specific identity theft
    // removal.  Combined §605B block + §609(e) records demand sent to
    // BOTH the consumer reporting agency AND the timeshare furnisher.
    // Every generation is LLM-rewritten per client per run.
    value: 'timeshare_identity_theft',
    label: 'Identity Theft — Fraudulent Timeshare Removal',
    tag: 'FCRA §605B + §609(e) · Combined CRA + Furnisher',
    desc: 'Combined identity-theft block (§605B) and business-records demand (§609(e)) sent to both the bureau and the timeshare furnisher. Blocks the entire timeshare tradeline (balance, delinquency, charge-off, foreclosure/forfeiture, collection items) within four business days. Now also carries an INDEPENDENT transparency / TILA §1601 disclosure-violation ground so the tradeline is deletable even if §605B were denied. Uniquely reworded per client.',
    enabled: true,
  },
  {
    // 2026-02-XX (user request) — Transparency / disclosure-violation
    // direct-removal demand.  Cites TILA §1601 et seq. / Regulation Z
    // and FCRA §1681e(b) accuracy duty.  LLM personalizes per client.
    value: 'transparency_violation',
    label: 'Transparency Violation — Direct Removal',
    tag: 'TILA §1601 · FCRA §1681e(b) · Direct Deletion',
    desc: 'Direct-removal demand for accounts where the furnisher failed to make required consumer-credit disclosures (TILA §1601 / Regulation Z). Cites FCRA §1681e(b) accuracy duty. Refuses dispute notations, suppressions, or cosmetic updates — demands full deletion. Uniquely reworded per client.',
    enabled: true,
  },
  {
    // 2026-02-XX (user request) — Military Lending Act direct-removal
    // demand.  10 U.S.C. §987 / 32 C.F.R. Part 232; §987(f)(3) voids
    // the agreement from inception if the MAPR / disclosure / prohibited-
    // provisions rules were violated while credit was extended to a
    // covered borrower.  LLM personalizes per client (branch of service,
    // dates of covered status, specific violation).
    value: 'military_lending_act',
    label: 'Military Lending Act — Direct Removal',
    tag: '10 U.S.C. §987 · Void ab initio',
    desc: 'Direct-removal demand for covered-borrower obligations that violated the Military Lending Act. §987(f)(3) voids the agreement from inception, so continued adverse reporting is a §1681e(b) accuracy violation. Uniquely reworded per client (branch, dates of covered status, specific MAPR / disclosure violation).',
    enabled: true,
  },
];

/**
 * DisputeLetterModal — 4-step wizard
 *   1. TYPE      → Regular or Second Round
 *   2. CONFIG    → Bureau + accounts (structured, grouped) + signature choice
 *   3. DRAFT     → LLM writes a UNIQUE letter body → editable preview
 *   4. FINALIZE  → PDF generation (typed or handwritten w/ handwriting picker)
 */
export default function DisputeLetterModal({
  open,
  onClose,
  clientName,
  clientAddressLines,
  defaultAccounts,       // legacy: array of pre-formatted strings
  defaultAccountsStructured, // preferred: [{ account_name, category, date_opened, balance }]
  onDone,
  // Feb 2026 — Saved Dispute Letters flow
  clientId,             // FTC client id for scoping saved letters
  initialSavedLetter,   // full saved letter doc to hydrate the modal with
  savedLetterId,        // if set, upsert (PATCH) that saved letter on generate
  onSaved,              // callback fired after a saved letter is created/updated
  // Feb 6 2026 — bubble the fresh Sworn Statement + Dispute Letter blobs up
  // to the parent so the Dispute Packet Builder can offer them as
  // checkboxes ("use the docs I just downloaded") instead of forcing the
  // user to browse the file system on mobile.
  onDisputeBlobsCaptured,
}) {
  const [step, setStep] = useState(1);

  // Step 1 — letter type. Single-select today; when the user checks a
  // second type we flip into MULTI-LETTER MODE (see letterTypes below).
  const [letterType, setLetterType] = useState('regular');
  // Pre-load the letter picked per account in the FTC "Recommended Dispute Letters" pop-up.
  const aiPicksTypesRef = useRef(null);
  useEffect(() => {
    if (!open || initialSavedLetter) return;
    const valid = new Set(LETTER_TYPES.map((lt) => lt.value));
    const assign = {};
    (defaultAccountsStructured || []).forEach((a) => {
      const t = pickFor(a.account_name);
      if (t && valid.has(t)) assign[a.account_name] = [t];
      else if (t === CUSTOM_PICK) assign[a.account_name] = [];
    });
    const types = [...new Set(Object.values(assign).flat())];
    if (!types.length) return;
    aiPicksTypesRef.current = [...types].sort().join(',');
    setLetterTypes(types);
    setLetterType(types[0]);
    if (types.length > 1 || Object.values(assign).some((v) => !v.length)) setLetterAssignments(assign);
  }, [open]);
  const customLetters = (defaultAccountsStructured || []).map((a) => customLetterFor(a.account_name)).filter(Boolean)
    .filter((c, i, arr) => arr.findIndex((x) => x.account_name === c.account_name) === i);
  const customDownloadedRef = useRef(false);
  const downloadCustomLetters = async () => {
    for (const c of customLetters) {
      try { await downloadCustomLetterPdf(c); } catch (e) { toast.error(`Custom letter PDF failed for ${c.account_name}`); }
    }
  };

  // 2026-02-08 — Multi-letter mode. `letterTypes` is the full set of
  // letter types the user has checked. When length > 1 the wizard shows
  // per-account letter checkboxes on Step 2 (accounts can belong to
  // multiple letters), then queues each type through Draft → Finalize
  // sequentially so the user can review each PDF before hitting Next.
  const [letterTypes, setLetterTypes] = useState(['regular']);
  // 2026-02-XX (user request "add short / straight-to-the-point variant") —
  // Per-letter length toggle.  Keyed by letter-type value → "full" |
  // "short".  Defaults to "full" if not set.  Radio sits inside each
  // letter-type card on Step 1 so it never clutters the main flow.
  const [letterLengths, setLetterLengths] = useState({});
  const getLetterLength = (lt) => letterLengths[lt] || 'short';
  const setLetterLength = (lt, len) =>
    setLetterLengths((prev) => ({ ...prev, [lt]: len }));
  // Map<accountId, Set<letterTypeValue>> — which letters each account
  // should be included in. Empty set = the account is off entirely.
  const [letterAssignments, setLetterAssignments] = useState({});
  // 2026-02-28 (user request) — Per-bureau account routing.  Maps
  // `aid → ['experian','equifax','transunion']`.  Default: an account
  // reports on all 3 bureaus.  Admin unchecks the ones that DON'T
  // report that account.  Used to:
  //   • Hide bureau header blocks for bureaus with zero tagged accounts
  //     ("Combined" mode)
  //   • Produce 1 letter per bureau with only its tagged accounts
  //     ("Separate" mode)
  const [accountBureaus, setAccountBureaus] = useState({});
  // 'combined' = one letter (header shows only bureaus with ≥1 tagged
  //              account, body lists all accounts)
  // 'separate' = up to 3 letters, one per bureau that has ≥1 tagged
  //              account, each letter filtered to its own accounts
  const [multiBureauMode, setMultiBureauMode] = useState('combined');
  // 2026-02-XX (user request "per-bureau inquiry disputes") —
  // When the picked letter_type is `inquiry_removal` the user needs to
  // say WHICH bureau(s) each inquiry sits on.  Keyed by account_id →
  // `{experian, equifax, transunion}` booleans.  Default when the
  // inquiry is first parsed: all three true (replicates the current
  // single-combined-letter behaviour).  When ANY row has a bureau
  // UN-checked, the generator produces ONE letter PER bureau that
  // still has at least one inquiry — each letter contains ONLY that
  // bureau's address block and ONLY the inquiries checked for it.
  const [inquiryBureauMatrix, setInquiryBureauMatrix] = useState({});
  const getInquiryBureaus = (aid) =>
    inquiryBureauMatrix[aid] || { experian: true, equifax: true, transunion: true };
  const setInquiryBureau = (aid, bureau, checked) =>
    setInquiryBureauMatrix((prev) => ({
      ...prev,
      [aid]: { ...getInquiryBureaus(aid), [bureau]: !!checked },
    }));
  const isInquiryMode = () => (letterTypes || []).includes('inquiry_removal');
  // Queue-driver state (only used in multi-mode).
  const [letterQueue, setLetterQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const isMulti = letterTypes.length > 1;

  // 2026-02-08 — Bankruptcy-specific optional fields. Only rendered on
  // Step 2 when the user has picked the Bankruptcy letter type (single
  // or multi mode). Forwarded to the draft endpoint so the LLM can weave
  // them into every unique generation.
  const [bankruptcyChapter, setBankruptcyChapter] = useState('');
  const [bankruptcyFilingDate, setBankruptcyFilingDate] = useState('');
  const [bankruptcyCourt, setBankruptcyCourt] = useState('');
  const [bankruptcyCaseNumber, setBankruptcyCaseNumber] = useState('');
  const bankruptcyPicked = letterTypes.includes('bankruptcy');

  // 2026-02-28 (user request) — Late-Payments specialised entry flow.
  // Toggled on when the user checks the "Late Payments Dispute Letter"
  // tile.  Holds the last-generated PDF blob + its filename so the
  // Finalize step can download it just like other letter types.
  const [showLatePaymentsModal, setShowLatePaymentsModal] = useState(false);
  const [showBusinessInfo, setShowBusinessInfo] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({});
  const [latePaymentsBlob, setLatePaymentsBlob] = useState(null);
  const [latePaymentsFilename, setLatePaymentsFilename] = useState(null);

  const toggleLetterType = (value) => {
    if (value === 'business' && !letterTypes.includes('business')) setShowBusinessInfo(true);
    setLetterTypes((prev) => {
      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      // Never allow zero selections — fall back to Regular.
      const clamped = next.length === 0 ? ['regular'] : next;
      // Keep the currently-active single letterType pointed at the first
      // selection so all the existing single-letter code paths still
      // work when we're in single-mode.
      setLetterType(clamped[0]);
      return clamped;
    });
    // 2026-02-28 (user request) — Late Payments is now a specialised
    // structured flow: picking it pops the LatePaymentsDisputeModal so
    // the admin can capture creditor + per-month severity + evidence
    // screenshot, then generate the aggressive FCRA template.  We only
    // pop it on the CHECK (not on uncheck), and only if it wasn't
    // already checked, so re-clicks after cancel don't spam.
    if (value === 'late_payments' && !letterTypes.includes('late_payments')) {
      setShowLatePaymentsModal(true);
    }
  };

  // Step 2 — config
  const [bureau, setBureau] = useState('all');

  // Feb 2026 — when the user picks the ChexSystems dispute letter type,
  // auto-set the bureau to `chexsystems` so the address block, subject,
  // and LLM body all target ChexSystems.  Bureau picker is hidden on
  // that letter type (there's only one target).  Switching AWAY from
  // ChexSystems resets the bureau back to "all" (the default) unless
  // the admin already picked a specific credit bureau.
  useEffect(() => {
    if (letterType === 'chexsystems') {
      setBureau('chexsystems');
    } else if (letterType === 'business') {
      setBureau('business');
    } else if (bureau === 'chexsystems' || bureau === 'business') {
      setBureau('all');
    }
  }, [letterType]);
  const [accountsInput, setAccountsInput] = useState('');

  // Hydrate the accounts textarea every time the modal (re)opens or the
  // incoming account props change. Without this, the lazy useState above
  // captured an empty prop at mount and the wizard was permanently stuck
  // at "0 accounts parsed" when opened from ASB.
  useEffect(() => {
    if (!open) return;

    // Feb 2026 — if the caller supplied a saved-letter doc, hydrate every
    // field the user picked last time and jump straight to Step 3 so they
    // only have to change the font/date/body they came back for.
    if (initialSavedLetter && typeof initialSavedLetter === 'object') {
      const s = initialSavedLetter;
      setLetterType(s.letter_type || 'regular');
      setBureau(s.bureau || 'all');
      setSubject(s.subject || '');
      setPreBody(s.pre_body || '');
      setPostBody(s.post_body || '');
      setGroupedAccounts(Array.isArray(s.accounts_grouped) ? s.accounts_grouped : []);
      setSignatureFont(s.signature_font || 'cursive_elegant');
      setBodyFont(s.body_font || 'arial');
      setStyle(s.style || 'handwritten');
      setHwStyle(s.handwriting_style || 'caveat');
      setHwFinish(s.handwriting_finish || 'original');
      setLetterDate(s.letter_date || '');
      setSavedId(s.id || savedLetterId || null);
      // Rebuild the accounts textarea from the saved account list.
      if (Array.isArray(s.accounts) && s.accounts.length) {
        const text = s.accounts.map((a) => {
          const bal = a.balance ? (String(a.balance).startsWith('$') ? a.balance : `$${a.balance}`) : '';
          const parts = [a.account_name, (a.category || 'OTHER').toUpperCase(), a.date_opened || '', bal].filter(Boolean);
          return parts.join(' | ');
        }).join('\n');
        setAccountsInput(text);
      }
      // Jump to Step 3 (draft preview) so the user can tweak wording/date/font
      // rather than restart the wizard.
      setStep(3);
      setDraftSource(s.subject || s.pre_body ? 'saved' : 'template');
      return;
    }

    if (Array.isArray(defaultAccountsStructured) && defaultAccountsStructured.length) {
      const text = defaultAccountsStructured.map((a) => {
        const bal = a.balance ? (String(a.balance).startsWith('$') ? a.balance : `$${a.balance}`) : '';
        const parts = [a.account_name, (a.category || 'OTHER').toUpperCase(), a.date_opened || '', bal]
          .filter(Boolean);
        return parts.join(' | ');
      }).join('\n');
      setAccountsInput(text);
    } else if (Array.isArray(defaultAccounts) && defaultAccounts.length) {
      setAccountsInput(defaultAccounts.join('\n'));
    }
    setSavedId(savedLetterId || null);
    setLetterDate('');
    // Always start at Step 1 on re-open — cleaner UX for a returning user.
    setStep(1);
  }, [open, JSON.stringify(defaultAccountsStructured), JSON.stringify(defaultAccounts), JSON.stringify(initialSavedLetter), savedLetterId]);

  // Step 3 — draft (LLM-generated, editable)
  const [drafting, setDrafting] = useState(false);
  const [draftSource, setDraftSource] = useState('');
  const [subject, setSubject] = useState('');
  const [preBody, setPreBody] = useState('');
  const [postBody, setPostBody] = useState('');
  // 2026-02-XX (e-OSCAR overhaul) — per-generation randomization profile
  // from /dispute-letter/draft.  Forwarded to /dispute-letter/generate
  // so the renderer uses the SAME picks (date format, font jitter,
  // address layout, signature-block layout, trailing watermark) that
  // the LLM baked into the wording.
  const [renderProfile, setRenderProfile] = useState(null);
  const [groupedAccounts, setGroupedAccounts] = useState([]);
  // Multi-select checkboxes for bulk-assign.  Stores stable
  // "category:index" keys of every checked row.  Cleared whenever
  // groupedAccounts changes shape.
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [bulkTargetCategory, setBulkTargetCategory] = useState('CREDIT_CARD');

  // Step 4 — finalize
  // style: 'typed' | 'handwritten' | 'both' (generates BOTH PDFs in one click)
  // Feb 2026 — user request: default to 'handwritten' since it's the
  // most e-OSCAR-friendly format the app produces.
  const [style, setStyle] = useState('handwritten');
  const [signatureFont, setSignatureFont] = useState(() => getLetterStyle().signatureFont);
  const [bodyFont, setBodyFont] = useState('arial');
  const [hwStyle, setHwStyle] = useState(() => getLetterStyle().hwStyle);
  // Same handwriting + signature as the batch letters; changes here become the new default.
  useEffect(() => { saveLetterStyle({ hwStyle, signatureFont }); }, [hwStyle, signatureFont]);
  // "original" = looks like a fresh handwritten letter with a pen.
  // "copied"   = looks like a photocopy of a handwritten letter (slight
  //              rotation + toner-uniform ink + dust specks — set by the
  //              backend `handwriting_finish` param).
  const [hwFinish, setHwFinish] = useState('original');
  // Feb 2026 — sworn statement cover page toggle. Now defaults OFF; the
  // user requested an explicit Yes / Skip prompt at generate-time
  // rather than an auto-include, because some client packets don't
  // need the sworn statement.
  const [includeSwornStatement, setIncludeSwornStatement] = useState(false);
  // Yes/Skip prompt shown when the user clicks "Generate PDF" — asks
  // "Do you need a Sworn Statement of Personal Submission?".  Yes
  // downloads it alongside the letter, Skip proceeds without it.
  const [showSwornPrompt, setShowSwornPrompt] = useState(false);
  // 2026-09-25 (user request): when multiple letter types are queued
  // (Round 1 + Round 2 + Round 3 + Settlement …), the sworn-statement
  // Yes/Skip prompt used to fire on EVERY letter in the queue.  Cache
  // the user's first answer in a ref so subsequent letters just re-use
  // it — one prompt for the whole batch.  `null` means "not answered
  // yet for this queue"; reset every time the user starts a fresh
  // multi-letter run at step 2.
  const swornChoiceForQueueRef = useRef(null);
  // Feb 2026 — optional explicit letter date (yyyy-mm-dd) so the user can
  // re-generate a saved letter with a different date without redoing the
  // wizard.  Empty string means "use server's today-in-PT default".
  const [letterDate, setLetterDate] = useState('');
  // Feb 2026 — track the saved-letter doc id so successive downloads
  // update the same record instead of creating duplicates.
  const [savedId, setSavedId] = useState(null);
  const [generating, setGenerating] = useState(false);

  const parsedAccounts = useMemo(() => {
    return accountsInput
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        // Support "NAME | CATEGORY | DATE | BALANCE" OR plain "NAME - DATE - $BAL"
        const parts = line.split('|').map((s) => s.trim());
        if (parts.length >= 2) {
          return {
            account_name: parts[0],
            category: parts[1] || 'OTHER',
            date_opened: parts[2] || null,
            balance: parts[3] || null,
          };
        }
        return { account_name: line, category: 'OTHER', date_opened: null, balance: null };
      });
  }, [accountsInput]);

  // Client-side mirror of the backend `_normalize_category` + `CATEGORY_LABELS`
  // so Step 2's live preview matches what the PDF will render — grouped by
  // type with the exact category header the letter will show.
  //
  // Nov 2026 — user requested: remove "Collection" and "Credit Union",
  // add "Mobile", "Apartment Lease", plus common alternates
  // ("Rental Lease", "Bank Loan", "Insurance", "Cable / Internet").
  const CATEGORY_ORDER = [
    'AUTO', 'CREDIT_CARD', 'PERSONAL_LOAN', 'STUDENT_LOAN', 'BANK_LOAN',
    'MORTGAGE', 'APARTMENT_LEASE', 'RENTAL_LEASE',
    'UTILITY', 'MOBILE', 'CABLE_INTERNET', 'INSURANCE', 'MEDICAL', 'OTHER',
  ];
  const CATEGORY_LABELS = {
    AUTO: 'Auto Loan',
    CREDIT_CARD: 'Credit Card',
    PERSONAL_LOAN: 'Personal Loan',
    STUDENT_LOAN: 'Student Loan',
    BANK_LOAN: 'Bank Loan',
    MORTGAGE: 'Mortgage',
    APARTMENT_LEASE: 'Apartment Lease',
    RENTAL_LEASE: 'Rental Lease',
    UTILITY: 'Utility',
    MOBILE: 'Mobile',
    CABLE_INTERNET: 'Cable / Internet',
    INSURANCE: 'Insurance',
    MEDICAL: 'Medical',
    OTHER: 'Other',
  };
  const CATEGORY_ALIASES = {
    AUTO_LOAN: 'AUTO', AUTOMOTIVE: 'AUTO', REPOSSESSION: 'AUTO',
    CC: 'CREDIT_CARD', CARD: 'CREDIT_CARD', REVOLVING: 'CREDIT_CARD', RETAIL_CARD: 'CREDIT_CARD',
    STUDENT: 'STUDENT_LOAN', EDUCATION: 'STUDENT_LOAN',
    PERSONAL: 'PERSONAL_LOAN', INSTALLMENT_LOAN: 'PERSONAL_LOAN',
    BANK: 'BANK_LOAN', CREDIT_UNION: 'BANK_LOAN',
    APARTMENT: 'APARTMENT_LEASE', LEASE: 'APARTMENT_LEASE',
    RENTAL: 'RENTAL_LEASE', RENT: 'RENTAL_LEASE',
    MED: 'MEDICAL', HOSPITAL: 'MEDICAL',
    UTIL: 'UTILITY',
    TELECOM: 'MOBILE', PHONE: 'MOBILE', CELL: 'MOBILE', WIRELESS: 'MOBILE',
    CABLE: 'CABLE_INTERNET', INTERNET: 'CABLE_INTERNET', ISP: 'CABLE_INTERNET',
    // Retire old buckets — collections & credit unions map into
    // more accurate categories (Other / Bank Loan) so existing
    // reports keep loading without crashing.
    COLLECTION: 'OTHER', COLLECTIONS: 'OTHER', CHARGE_OFF: 'OTHER',
  };
  const normalizeCategory = (raw) => {
    if (!raw) return 'OTHER';
    const c = String(raw).trim().toUpperCase().replace(/-/g, '_').replace(/\s+/g, '_');
    if (CATEGORY_LABELS[c] !== undefined) return c;
    return CATEGORY_ALIASES[c] || 'OTHER';
  };

  // Grouped view of the pasted accounts — mirrors the backend PDF grouping
  // so the user sees the exact ordering / headers the letter will use.
  const parsedAccountsGrouped = useMemo(() => {
    const buckets = {};
    for (const a of parsedAccounts) {
      const cat = normalizeCategory(a.category);
      (buckets[cat] = buckets[cat] || []).push(a);
    }
    const out = [];
    const seen = new Set();
    for (const cat of CATEGORY_ORDER) {
      if (buckets[cat]) {
        out.push({ category: cat, label: CATEGORY_LABELS[cat] || '', items: buckets[cat] });
        seen.add(cat);
      }
    }
    for (const [cat, items] of Object.entries(buckets)) {
      if (!seen.has(cat)) {
        out.push({ category: cat, label: CATEGORY_LABELS[cat] || '', items });
      }
    }
    return out;
  }, [parsedAccounts]);

  // 2026-02-XX (user request "each letter needs to be precise") —
  // Smart account auto-assignment when specialty letter types are
  // added to the multi-letter mix.  Detects likely-matching accounts
  // by name keyword and assigns them EXCLUSIVELY to the specialty
  // letter (unchecking from the general "regular"/round letters).
  // Prevents the "both letters have all 5 accounts" bug where the
  // user picks Regular + Timeshare and every letter dumps every account.
  //
  // Bugfix note: Originally placed above `parsedAccounts = useMemo(...)`
  // which threw "Cannot access 'parsedAccounts' before initialization"
  // (temporal dead zone) and blackscreen'd every code path that
  // instantiated DisputeLetterModal — including the FTC report loader.
  // Keep this block AFTER `parsedAccounts` / `parsedAccountsGrouped`.
  const SPECIALTY_KEYWORDS = {
    timeshare_identity_theft: [
      'timeshare', 'wyndham', 'diamond resort', 'bluegreen', 'westgate',
      'hilton grand', 'marriott vacation', 'vive financial', 'vive',
      'silverleaf', 'holiday inn club', 'orange lake', 'welk', 'travel and leisure',
      'sheraton vacation', 'grand pacific', 'starwood vacation', 'shell vacation',
      'wgv', 'monera', 'quorum federal',
    ],
    bankruptcy: ['bankruptcy', 'trustee', 'chapter 7', 'chapter 13', 'ch 7', 'ch 13'],
    identity_theft_bankruptcy: [
      'included in bankruptcy', 'bankruptcy discharge', 'bkrpt',
    ],
    identity_theft_settled: [
      'settled', 'paid in settlement', 'settlement',
    ],
    chexsystems: [], // ChexSystems letters use a different flow entirely
    business: [],
    child_support: [
      'child support', 'child-support', 'friend of court', 'child sup',
    ],
    inquiry_removal: [],   // inquiry_removal handles inquiries, not tradelines
    late_payments: [],     // handled by dedicated modal
    transparency_violation: [], // universal — apply per manual override
    military_lending_act: [
      'navy federal', 'usaa', 'penfed', 'servicemember', 'navy-federal',
      'military', 'armed forces', 'marine federal',
    ],
  };
  const GENERAL_LETTER_TYPES = new Set(['regular', 'second_round', 'third_round']);

  // Auto-assign specialty accounts whenever the letter mix or account
  // list changes.  Runs ONCE per (letterTypes, parsedAccounts) pair —
  // we intentionally do not clobber a user's manual overrides.
  const _autoAssignedRunRef = useRef({ types: '', accts: '' });
  useEffect(() => {
    const typesKey = [...letterTypes].sort().join(',');
    const acctsKey = parsedAccounts.map((a) => a.id || a.account_name).join('|');
    // Only run when the pair changes (prevents wiping manual edits).
    if (
      _autoAssignedRunRef.current.types === typesKey &&
      _autoAssignedRunRef.current.accts === acctsKey
    ) {
      return;
    }
    _autoAssignedRunRef.current = { types: typesKey, accts: acctsKey };
    // Keep the per-account AI picks instead of keyword auto-assignment.
    if (aiPicksTypesRef.current === typesKey) return;
    // Nothing to auto-assign in single-letter mode.
    if (letterTypes.length < 2 || parsedAccounts.length === 0) return;

    // Compute the auto-assignment map: start each account with EVERY
    // picked letter type, then move specialty-matched accounts to
    // exclusively their specialty letter.
    const activeSpecialties = letterTypes.filter(
      (lt) => (SPECIALTY_KEYWORDS[lt] || []).length > 0,
    );
    if (activeSpecialties.length === 0) return;

    const nextMap = {};
    for (const a of parsedAccounts) {
      const aid = a.id || a.account_name;
      const nm = String(a.account_name || '').toLowerCase();
      let matched = null;
      for (const lt of activeSpecialties) {
        if (SPECIALTY_KEYWORDS[lt].some((kw) => nm.includes(kw))) {
          matched = lt;
          break;
        }
      }
      if (matched) {
        // Specialty account → ONLY on its specialty letter.
        nextMap[aid] = [matched];
      } else {
        // Non-specialty account → only on general letters (Regular /
        // Round 2 / Round 3), never on specialty letters by default.
        const generalPicked = letterTypes.filter((lt) => GENERAL_LETTER_TYPES.has(lt));
        nextMap[aid] = generalPicked.length > 0 ? generalPicked : [letterTypes[0]];
      }
    }
    setLetterAssignments(nextMap);
  }, [letterTypes, parsedAccounts]);

  // ── Step 3 category editing (Feb 2026 revamp) ────────────────────
  // Users can move an account into a different category directly from
  // the "Accounts (grouped)" preview on Step 3.  Whatever they see in
  // that preview is EXACTLY what the PDF will render — WYSIWYG.
  const moveAccountToCategory = (fromCat, itemIdx, toCat) => {
    if (!toCat || fromCat === toCat) return;
    setGroupedAccounts((prev) => {
      // Find source group + account being moved
      const next = prev.map((g) => ({ ...g, items: [...g.items] }));
      const fromGroup = next.find((g) => g.category === fromCat);
      if (!fromGroup) return prev;
      const [account] = fromGroup.items.splice(itemIdx, 1);
      if (!account) return prev;
      // Update the account's own category tag for downstream PDF gen.
      account.category = toCat;
      // Merge into destination group (or create it if it doesn't exist).
      let toGroup = next.find((g) => g.category === toCat);
      if (!toGroup) {
        toGroup = {
          category: toCat,
          label: CATEGORY_LABELS[toCat] || "",
          items: [],
        };
        next.push(toGroup);
      }
      toGroup.items.push(account);
      // Drop any groups that became empty so the preview stays clean.
      const cleaned = next.filter((g) => g.items.length > 0);
      // Preserve CATEGORY_ORDER for the visible sort.
      cleaned.sort((a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.category);
        const bi = CATEGORY_ORDER.indexOf(b.category);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      return cleaned;
    });
  };

  // ── Bulk-assign ────────────────────────────────────────────────
  // Snapshot the current account by stable key so we can pluck matching
  // entries out of the grouped structure in one shot.
  const toggleSelected = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const selectAllVisible = () => {
    const keys = new Set();
    groupedAccounts.forEach((g) => {
      g.items.forEach((_, i) => keys.add(`${g.category}:${i}`));
    });
    setSelectedKeys(keys);
  };
  const clearSelection = () => setSelectedKeys(new Set());

  const bulkMoveSelected = (toCat) => {
    if (!toCat || selectedKeys.size === 0) return;
    setGroupedAccounts((prev) => {
      const next = prev.map((g) => ({ ...g, items: [...g.items] }));
      // Collect all accounts to move first, then remove them from source
      // groups (removing back-to-front so surviving indices stay valid).
      const buckets = new Map();
      next.forEach((g) => { buckets.set(g.category, g); });
      const pulled = [];
      selectedKeys.forEach((key) => {
        const [cat, idxStr] = key.split(':');
        const g = buckets.get(cat);
        if (!g) return;
        const idx = parseInt(idxStr, 10);
        pulled.push({ cat, idx, ref: g.items[idx] });
      });
      // Remove back-to-front per category to keep indices consistent.
      const byCat = new Map();
      pulled.forEach((p) => {
        if (!byCat.has(p.cat)) byCat.set(p.cat, []);
        byCat.get(p.cat).push(p.idx);
      });
      byCat.forEach((idxs, cat) => {
        const g = buckets.get(cat);
        if (!g) return;
        idxs.sort((a, b) => b - a).forEach((i) => { g.items.splice(i, 1); });
      });
      // Ensure target group exists.
      let toGroup = next.find((g) => g.category === toCat);
      if (!toGroup) {
        toGroup = {
          category: toCat,
          label: CATEGORY_LABELS[toCat] || 'Other',
          items: [],
        };
        next.push(toGroup);
      }
      // Append moved accounts with updated category tags.
      pulled.forEach((p) => {
        if (!p.ref) return;
        const moved = { ...p.ref, category: toCat };
        toGroup.items.push(moved);
      });
      const cleaned = next.filter((g) => g.items.length > 0);
      cleaned.sort((a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.category);
        const bi = CATEGORY_ORDER.indexOf(b.category);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      return cleaned;
    });
    setSelectedKeys(new Set());
  };

  // ── 2026-02-05 (user request) — Full inline account editing ────────
  // Users open a saved dispute letter and need to add / remove / edit
  // individual accounts without restarting the wizard.  Every mutation
  // updates `groupedAccounts` in place (single source of truth) and
  // `accountsForGeneration()` reflects it in the PDF automatically.
  const removeAccountFromGroup = (cat, idx) => {
    setGroupedAccounts((prev) => {
      const next = prev
        .map((g) => (g.category === cat
          ? { ...g, items: g.items.filter((_, i) => i !== idx) }
          : g))
        .filter((g) => g.items.length > 0);
      return next;
    });
    // Also drop from selection if it was checked.
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.delete(`${cat}:${idx}`);
      return next;
    });
  };

  const updateAccountField = (cat, idx, field, value) => {
    setGroupedAccounts((prev) => prev.map((g) => {
      if (g.category !== cat) return g;
      return {
        ...g,
        items: g.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)),
      };
    }));
  };

  const addAccountToGroup = (cat) => {
    setGroupedAccounts((prev) => {
      const next = prev.map((g) => ({ ...g, items: [...g.items] }));
      let toGroup = next.find((g) => g.category === cat);
      if (!toGroup) {
        toGroup = {
          category: cat,
          label: CATEGORY_LABELS[cat] || 'Other',
          items: [],
        };
        next.push(toGroup);
      }
      toGroup.items.push({
        account_name: '',
        category: cat,
        date_opened: '',
        balance: '',
      });
      // Preserve CATEGORY_ORDER for the visible sort.
      next.sort((a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.category);
        const bi = CATEGORY_ORDER.indexOf(b.category);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      return next;
    });
  };

  const addNewCategoryGroup = (cat) => {
    if (!cat) return;
    setGroupedAccounts((prev) => {
      if (prev.some((g) => g.category === cat)) return prev; // already exists
      const next = [
        ...prev,
        { category: cat, label: CATEGORY_LABELS[cat] || 'Other', items: [{ account_name: '', category: cat, date_opened: '', balance: '' }] },
      ];
      next.sort((a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.category);
        const bi = CATEGORY_ORDER.indexOf(b.category);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      return next;
    });
  };

  // Flatten `groupedAccounts` back to a category-tagged array so PDF
  // generation picks up any category re-assignments the user made on
  // Step 3.  Falls back to the raw `parsedAccounts` array if the user
  // hasn't advanced past Step 2 yet.
  const accountsForGeneration = () => {
    if (!groupedAccounts || groupedAccounts.length === 0) return parsedAccounts;
    const out = [];
    for (const g of groupedAccounts) {
      for (const item of g.items) {
        out.push({ ...item, category: g.category });
      }
    }
    return out;
  };

  if (!open) return null;

  const reset = () => {
    setStep(1);
    setDraftSource('');
    setSubject('');
    setPreBody('');
    setPostBody('');
    setGroupedAccounts([]);
    customDownloadedRef.current = false;
  };

  const close = () => {
    reset();
    onClose && onClose();
  };

  const generateDraft = async (letterTypeOverride, bureauOverride = null) => {
    if (!clientName?.trim()) {
      toast.error('Client name is required.');
      return;
    }
    // 2026-02-XX (bugfix) — In multi-letter mode the caller passes the
    // intended letter type EXPLICITLY so we don't read a stale value
    // from the React state closure (setLetterType + setTimeout does not
    // guarantee the new state is committed before the next tick).
    // Without this override, picking Regular + Timeshare on Step 1
    // caused BOTH letters to render as "regular" because the queue
    // advance read the previous render's letterType.
    const activeLetterType = letterTypeOverride || letterType;
    // 2026-02-XX (user request "per-bureau inquiry disputes") — When
    // the queue entry is a per-bureau object, override BOTH the bureau
    // and the accounts list so this specific letter targets ONLY the
    // picked bureau and ONLY the inquiries ticked for it.
    const activeBureau = bureauOverride?.bureau || bureau;
    setDrafting(true);
    try {
      // 2026-02-08 multi-letter mode — when the user picked multiple
      // letter types on Step 1, filter the parsed account list down to
      // just the accounts they assigned to the CURRENT letter type
      // (activeLetterType). Single-letter mode keeps every account.
      let accountsForThisLetter;
      if (bureauOverride?.accountIds) {
        // Per-bureau inquiry split — strict allow-list of inquiry ids.
        const allow = new Set(bureauOverride.accountIds);
        accountsForThisLetter = parsedAccounts.filter((a) =>
          allow.has(a.id || a.account_name));
      } else if (isMulti) {
        accountsForThisLetter = parsedAccounts.filter((a) => {
          const aid = a.id || a.account_name;
          const assigned = letterAssignments[aid] || letterTypes; // default all
          return assigned.includes(activeLetterType);
        });
      } else {
        // Accounts handled by a custom AI letter are left off the template letter.
        accountsForThisLetter = parsedAccounts.filter((a) => letterAssignments[a.id || a.account_name]?.length !== 0);
      }
      if ((isMulti || bureauOverride) && accountsForThisLetter.length === 0) {
        toast.error(`No accounts assigned to this letter — pick some on Step 2`);
        setDrafting(false);
        return;
      }
      const res = await axios.post(
        `${API}/api/dispute-letter/draft`,
        {
          client_name: clientName,
          bureau: activeBureau,
          letter_type: activeLetterType,
          accounts: accountsForThisLetter,
          // 2026-02-08 — optional bankruptcy fields; backend ignores them
          // for non-bankruptcy letter types.
          bankruptcy_chapter: bankruptcyChapter || null,
          bankruptcy_filing_date: bankruptcyFilingDate || null,
          bankruptcy_court: bankruptcyCourt || null,
          bankruptcy_case_number: bankruptcyCaseNumber || null,
          // 2026-02-XX — user picked "Full" or "Short" inside the
          // letter-type card on Step 1.
          letter_length: getLetterLength(activeLetterType),
          business_info: activeLetterType === 'business' ? businessInfo : null,
        },
        { timeout: 180000 },
      );
      // Keep letterType state in sync so downstream generate/save calls
      // read the correct type. (When an override is passed, the parent
      // has already called setLetterType, but we call it again defensively
      // in case that update was batched away.)
      if (letterTypeOverride && letterTypeOverride !== letterType) {
        setLetterType(letterTypeOverride);
      }
      setSubject(res.data.subject || '');
      setPreBody(res.data.pre_body || '');
      setPostBody(res.data.post_body || '');
      // 2026-02-XX — Capture the per-generation randomization profile so
      // when the user hits Generate PDF next, the renderer uses the
      // SAME picks (date format, font jitter, address layout, trailing
      // watermark) that the LLM used for the wording.
      setRenderProfile(res.data.render_profile || null);
      setGroupedAccounts(res.data.accounts_grouped || []);
      setDraftSource(res.data.source || 'template');
      setStep(3);
      if (res.data.source === 'llm') {
        toast.success('Draft generated (unique wording via AI)');
      } else {
        toast.info(activeLetterType === 'business' ? "Business letter ready — type each account's dispute reason below" : 'Draft loaded from template (LLM unavailable)');
      }
    } catch (e) {
      console.error('[Dispute Draft]', e);
      toast.error(`Draft failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDrafting(false);
    }
  };

  const regenerateDraft = async () => {
    // Re-call the draft endpoint to get a *different* unique wording
    await generateDraft();
    toast.success('Draft re-worded');
  };

  const generatePDF = async (includeSwornOverride) => {
    // Feb 2026 — Yes/Skip prompt passes an explicit boolean so we don't
    // race React state updates.  Fall back to the checkbox state.
    const shouldIncludeSworn = typeof includeSwornOverride === 'boolean'
      ? includeSwornOverride
      : includeSwornStatement;
    // 2026-09-25 — In multi-letter mode the sworn statement is the SAME
    // one-page PDF for every letter in the queue, so we only download it
    // ONCE (on the first letter of the run).  Skips the redundant
    // second/third/fourth downloads entirely.
    const isFirstOfQueue = queueIndex === 0;
    const downloadSwornThisPass = shouldIncludeSworn && (!isMulti || isFirstOfQueue);
    setGenerating(true);
    const stylesToRun = style === 'both' ? ['typed', 'handwritten'] : [style];
    const downloaded = [];
    // Capture the just-downloaded blobs so the Dispute Packet Builder
    // can offer them as checkboxes instead of file uploads (per user
    // request 2026-02-06).
    const captured = { swornBlob: null, swornName: null, disputeBlob: null, disputeName: null };
    try {
      // Step 1 — sworn statement (Feb 2026, user request).  Delivered as
      // its own single-page PDF right before the dispute letter downloads,
      // so the user can review + sign it separately.  Skipped if the
      // toggle in the config panel was disabled OR if we've already
      // downloaded the sworn statement earlier in this multi-letter run.
      if (downloadSwornThisPass) {
        toast.info('Downloading sworn statement…', { id: 'dispute-sworn' });
        try {
          // Feb 2026 — mirror the dispute letter's chosen style onto
          // the sworn statement so both PDFs match visually.  When the
          // user picks "handwritten", the sworn statement is rendered
          // via the same handwriting_converter with the same font +
          // tool + paper + finish selection.
          const swornStyleForRun = stylesToRun.includes('handwritten') ? 'handwritten' : 'typed';
          const swornRes = await axios.post(
            `${API}/api/dispute-letter/sworn-statement`,
            {
              client_name: clientName,
              bureau,
              letter_type: letterType,
              signature_font: signatureFont,
              body_font: bodyFont,
              style: swornStyleForRun,
              handwriting_style: hwStyle,
              handwriting_finish: hwFinish,
            },
            { responseType: 'blob', timeout: 120000 },
          );
          const swornCd = swornRes.headers?.['content-disposition'] || '';
          const swornMatch = swornCd.match(/filename="([^"]+)"/);
          const swornName = swornMatch?.[1] || `${clientName.replace(/\s+/g, '_')}_sworn_statement.pdf`;
          // Retain blob for the Dispute Packet Builder checklist.
          captured.swornBlob = swornRes.data;
          captured.swornName = swornName;
          const swornUrl = window.URL.createObjectURL(swornRes.data);
          const sa = document.createElement('a');
          sa.href = swornUrl; sa.download = swornName;
          document.body.appendChild(sa); sa.click(); sa.remove();
          window.URL.revokeObjectURL(swornUrl);
          downloaded.push(swornName);
          toast.success(`Sworn statement downloaded — ${swornName}`, { id: 'dispute-sworn' });
          // Small delay so browsers don't coalesce this with the next download.
          await new Promise((r) => setTimeout(r, 400));
        } catch (swornErr) {
          console.warn('[Sworn Statement]', swornErr);
          toast.error('Sworn statement download failed — continuing with the dispute letter.', { id: 'dispute-sworn' });
        }
      }

      // 2026-02-28 (user request) — Resolve the per-bureau routing BEFORE
      // the style loop so we can either (a) call generate once with the
      // bureau-filtered header ("combined" mode) or (b) call it up to 3
      // times, once per bureau ("separate" mode).
      const _allBureaus = ['experian', 'equifax', 'transunion'];
      const _accountsNow = accountsForGeneration();
      const _bureausFor = (aid) => {
        const v = accountBureaus[aid];
        return Array.isArray(v) && v.length ? v : _allBureaus.slice();
      };
      // Build the generation plan — array of { bureausToShow, accounts, label }
      let _plan = [];
      if (bureau === 'all' && multiBureauMode === 'separate') {
        // Separate mode: one letter per bureau with ≥1 tagged account.
        for (const b of _allBureaus) {
          const acctsForB = _accountsNow.filter((a) => {
            const aid = a.id || a.account_name;
            return _bureausFor(aid).includes(b);
          });
          if (acctsForB.length === 0) continue;   // skip bureaus with no accounts
          _plan.push({ bureausToShow: [b], accounts: acctsForB, label: b });
        }
        if (_plan.length === 0) _plan.push({ bureausToShow: null, accounts: _accountsNow, label: 'all' });
      } else if (bureau === 'all') {
        // Combined: ONE letter.  Header shows only bureaus that at least
        // one account is tagged for.
        const tagged = new Set();
        for (const a of _accountsNow) {
          for (const b of _bureausFor(a.id || a.account_name)) tagged.add(b);
        }
        const bureausToShow = _allBureaus.filter((b) => tagged.has(b));
        _plan.push({
          bureausToShow: bureausToShow.length === 3 ? null : bureausToShow,
          accounts: _accountsNow,
          label: 'all',
        });
      } else {
        // User picked a specific bureau — nothing to split, honour it.
        _plan.push({ bureausToShow: null, accounts: _accountsNow, label: bureau });
      }

      // Step 2 — dispute letter itself (may run twice if style === 'both').
      for (const planItem of _plan) {
        const _effectiveBureau = (bureau === 'all' && planItem.label !== 'all')
          ? planItem.label        // separate mode — target single bureau
          : bureau;
      for (const s of stylesToRun) {
        if (stylesToRun.length > 1) {
          toast.info(s === 'handwritten'
            ? 'Rendering handwritten version — this can take 15–30s…'
            : 'Rendering typed version…');
        }
        const res = await axios.post(
          `${API}/api/dispute-letter/generate`,
          {
            client_name: clientName,
            client_address_lines: clientAddressLines || [],
            bureau: _effectiveBureau,
            // 2026-07-22 fix — pass the accounts as-currently-grouped on
            // Step 3 so any category re-assignments the user made in the
            // preview flow through to the PDF.  Falls back to parsedAccounts
            // if Step 3 hasn't been reached (edge case).
            accounts: planItem.accounts,
            letter_type: letterType,
            subject,
            pre_body: preBody,
            post_body: postBody,
            signature_font: signatureFont,
            body_font: bodyFont,
            style: s,
            handwriting_style: hwStyle,
            handwriting_finish: hwFinish,
            // Feb 2026 — user-picked date override so re-rendered saved
            // letters carry the date the user selected here rather than
            // today's PT date.  Empty string → backend uses PT today.
            letter_date: letterDate || null,
            // Sworn cover is now delivered as its own PDF above — never
            // merge it into the letter PDF anymore.
            include_sworn_statement: false,
            // 2026-02-XX (e-OSCAR overhaul) — forward the per-generation
            // randomization profile so the PDF layer honours the SAME
            // picks (date format, font jitter, address layout, trailing
            // watermark) the LLM baked into the wording.
            render_profile: renderProfile || null,
            letter_length: getLetterLength(letterType),
            // 2026-02-28 (user request) — Per-bureau header routing.
            // In "combined" mode this limits the header blocks to the
            // bureaus with ≥1 tagged account; in "separate" mode it's
            // null since the single-bureau request already handles it.
            bureaus_to_show: planItem.bureausToShow,
          },
          { responseType: 'blob', timeout: 240000 },
        );
        const cd = res.headers?.['content-disposition'] || '';
        const match = cd.match(/filename="([^"]+)"/);
        let filename = match?.[1] || `dispute_letter_${_effectiveBureau}.pdf`;
        // Distinguish typed vs. handwritten filenames when generating both.
        // Feb 2026 — user changed the base filename to "JT dispute letter.pdf"
        // (initials + " dispute letter.pdf"). Keep that clean by using a
        // parenthesized suffix rather than an underscore.
        if (stylesToRun.length > 1) {
          filename = filename.replace(/\.pdf$/i, s === 'handwritten' ? ' (handwritten).pdf' : ' (typed).pdf');
        }
        // Retain the LAST-generated dispute-letter blob (typed if both,
        // handwritten if solo handwritten) so the packet builder can
        // reference it via checkbox.
        if (!(await previewPdf(res.data, filename))) continue;  // previewed, user cancelled this one
        captured.disputeBlob = res.data;
        captured.disputeName = filename;
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        downloaded.push(filename);
      }
      // Small delay between separate-bureau letters so the browser
      // doesn't coalesce multiple downloads into one prompt.
      if (_plan.length > 1) {
        await new Promise((r) => setTimeout(r, 500));
      }
      }  // ← end _plan loop
      if (downloaded.length) toast.success(
        downloaded.length > 1
          ? `${downloaded.length} PDFs downloaded (sworn statement + letter${stylesToRun.length > 1 ? 's' : ''})`
          : `Dispute letter downloaded — ${downloaded[0]}`,
      );

      // ─── Feb 2026 — Save/upsert this letter to the client's profile ───
      // Persist the FULL state (bureau, accounts, wording, font, date)
      // so the user can reopen this exact letter later and just change
      // the font/date without redoing the wizard.  Failures here are
      // logged but never block the download UX.
      try {
        const savedPayload = {
          client_id: clientId || null,
          client_name: clientName,
          client_address_lines: clientAddressLines || [],
          bureau,
          letter_type: letterType,
          subject: subject || null,
          pre_body: preBody || null,
          post_body: postBody || null,
          accounts: accountsForGeneration(),
          accounts_grouped: groupedAccounts,
          signature_font: signatureFont,
          body_font: bodyFont,
          style,
          handwriting_style: hwStyle,
          handwriting_finish: hwFinish,
          letter_date: letterDate || null,
        };
        let savedDoc;
        if (savedId) {
          const patchRes = await axios.patch(
            `${API}/api/saved-dispute-letters/${savedId}`,
            savedPayload,
            { timeout: 15000 },
          );
          savedDoc = patchRes.data;
        } else {
          const postRes = await axios.post(
            `${API}/api/saved-dispute-letters`,
            savedPayload,
            { timeout: 15000 },
          );
          savedDoc = postRes.data;
          setSavedId(savedDoc?.id || null);
        }
        if (onSaved) onSaved(savedDoc);
      } catch (saveErr) {
        console.warn('[Dispute Letter] save-state failed (non-blocking):', saveErr);
      }

      if (onDone) onDone({ bureau, letterType, style, files: downloaded });
      // Bubble the captured blobs up so the Dispute Packet Builder can
      // offer them as checkboxes (skips the file-picker on mobile).
      if (onDisputeBlobsCaptured && (captured.swornBlob || captured.disputeBlob)) {
        try { onDisputeBlobsCaptured(captured); } catch (err) { console.warn('[Dispute Blobs Cb]', err); }
      }
      // 2026-02-08 multi-letter queue advance. If more letter types are
      // waiting, don't close — bump to the next one, wipe the draft
      // state, and re-run generateDraft against its filtered account
      // list so the user sees the fresh LLM wording for that letter.
      // 2026-02-XX — Queue entries can now be STRINGS ('regular',
      // 'bankruptcy') OR per-bureau OBJECTS ({letterType, bureau,
      // accountIds}) when inquiry_removal was split across bureaus.
      // Normalize both shapes here.
      const queueActive = (letterQueue || []).length > 0;
      if (queueActive && queueIndex + 1 < letterQueue.length) {
        const nextIdx = queueIndex + 1;
        const nextEntry = letterQueue[nextIdx];
        const nextType = typeof nextEntry === 'string' ? nextEntry : nextEntry.letterType;
        const nextOverride = typeof nextEntry === 'string' ? null : nextEntry;
        setQueueIndex(nextIdx);
        setLetterType(nextType);
        if (nextOverride?.bureau) setBureau(nextOverride.bureau);
        setSubject('');
        setPreBody('');
        setPostBody('');
        setGroupedAccounts([]);
        setStep(3);
        // 2026-02-XX (bugfix) — Pass nextType EXPLICITLY so generateDraft
        // doesn't read the stale letterType from the closure.  Previously
        // this made every letter in the queue generate as the FIRST
        // letter's type (e.g. picking Regular + Timeshare produced two
        // Regular letters both listing all accounts).
        setTimeout(() => generateDraft(nextType, nextOverride), 40);
        const label = nextOverride?.bureau ? `${nextType} (${nextOverride.bureau})` : nextType;
        toast.success(`Next up: ${label} letter (${nextIdx + 1} of ${letterQueue.length})`);
        return;
      }
      if (queueActive) {
        toast.success(`All ${letterQueue.length} letters downloaded ✓`);
      }
      if (customLetters.length && !customDownloadedRef.current) {
        customDownloadedRef.current = true;
        await downloadCustomLetters();
        toast.success(`${customLetters.length} custom AI letter${customLetters.length > 1 ? 's' : ''} downloaded`);
      }
      close();
    } catch (e) {
      console.error('[Dispute PDF]', e);
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || text;
        } catch { /* ignore */ }
      }
      toast.error(`PDF generation failed: ${msg}`);
    } finally {
      setGenerating(false);
    }
  };

  // ─────────────────────────── Renderers ───────────────────────────
  const renderHeader = () => (
    <div className="flex items-center justify-between p-5 border-b border-white/10">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center">
          <FileText className="w-5 h-5 text-fuchsia-300" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Dispute Letter Generator</h3>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>Step {step} of 4</span>
            <span>·</span>
            <span>
              {step === 1 && 'Choose letter type'}
              {step === 2 && 'Configure accounts & bureau'}
              {step === 3 && 'Review & edit unique wording'}
              {step === 4 && 'Finalize signature & download'}
            </span>
          </div>
        </div>
      </div>
      <button
        onClick={close}
        className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/[0.06]"
        aria-label="Close"
        data-testid="dispute-modal-close"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );

  const renderStep1 = () => (
    <div className="p-5 space-y-3 max-h-[80vh] overflow-y-auto">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
        What kind of letter are we sending?
      </div>
      {/* 2026-02-28 — Compact 2-column grid.  Old vertical stack made
          all 11 letter types spill below the fold on 13-inch and mobile
          screens (Feb 2026 user report: "won't let you scroll up past
          Third Round").  Grid keeps every card small, gives the modal
          an internal max-height + overflow so scroll always works, and
          `items-start` on the outer container guarantees the TOP of the
          card is reachable even when content is taller than the viewport. */}
      <div className="text-[11px] text-slate-500 -mt-1 mb-2">
        Tick multiple to build several letters at once — each account can
        go to one or more letters.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {LETTER_TYPES.map((t) => {
          const checked = letterTypes.includes(t.value);
          return (
            <button
              key={t.value}
              onClick={() => t.enabled && toggleLetterType(t.value)}
              disabled={!t.enabled}
              title={t.desc}
              className={`text-left p-3 rounded-lg border transition-all ${
                checked
                  ? 'bg-fuchsia-600/20 border-fuchsia-400/70 ring-1 ring-fuchsia-400/40'
                  : 'bg-black/30 border-white/10 hover:border-fuchsia-400/40 hover:bg-fuchsia-600/5'
              } ${t.enabled ? '' : 'opacity-50 cursor-not-allowed'}`}
              data-testid={`dispute-type-${t.value}`}
            >
              <div className="flex items-start gap-2">
                <div
                  className={`w-4 h-4 mt-0.5 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                    checked ? 'bg-fuchsia-500 border-fuchsia-300' : 'border-slate-500'
                  }`}
                  aria-hidden="true"
                >
                  {checked && <span className="text-white text-[10px] leading-none">✓</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white text-sm leading-tight">{t.label}</div>
                  <div className="text-[10px] text-fuchsia-300/90 font-mono mt-0.5 truncate">{t.tag}</div>
                  {/* 2026-02-XX (user request "add short / straight-to-the-point variant") — */}
                  {/* Full vs Short radio sits INSIDE each letter-type card so the user  */}
                  {/* picks the length at the same moment they pick the letter.  Only    */}
                  {/* appears once the type is checked so the Step 1 grid stays clean.   */}
                  {checked && (
                    <div
                      className="mt-2 flex items-center gap-3 text-[10px]"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`length-radio-wrap-${t.value}`}
                    >
                      <label className="flex items-center gap-1 cursor-pointer text-cyan-200">
                        <input
                          type="radio"
                          name={`length-${t.value}`}
                          checked={getLetterLength(t.value) === 'short'}
                          onChange={() => setLetterLength(t.value, 'short')}
                          className="accent-cyan-400 w-3 h-3"
                          data-testid={`length-${t.value}-short`}
                        />
                        <span className="font-mono">Short · 2-3 pg (default)</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer text-emerald-200">
                        <input
                          type="radio"
                          name={`length-${t.value}`}
                          checked={getLetterLength(t.value) === 'full'}
                          onChange={() => setLetterLength(t.value, 'full')}
                          className="accent-emerald-400 w-3 h-3"
                          data-testid={`length-${t.value}-full`}
                        />
                        <span className="font-mono">Long · 5-8 pg</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {isMulti && (
        <div
          className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-200"
          data-testid="dispute-multi-mode-banner"
        >
          <span className="font-bold">Multi-letter mode active</span> — {letterTypes.length} letters
          will be built. Next step: choose which accounts go on each one (an account can appear on more than one).
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="p-5 space-y-4">
      {/* Sender */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Sender</div>
        <div className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100">
          <div className="font-semibold">{clientName || '—'}</div>
          {(clientAddressLines || []).map((l, i) => (
            <div key={i} className="text-xs text-slate-400">{l}</div>
          ))}
        </div>
      </div>

      {/* Bureau */}
      {letterType === 'business' && (
        <div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-slate-300" data-testid="business-recipients-note">
          Recipients: Equifax Commercial Solutions · Experian Business · Dun &amp; Bradstreet (one letter, all three addresses at the top).
        </div>
      )}
      <div className={letterType === 'business' ? 'hidden' : ''}>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Recipient Bureau</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { v: 'all', l: 'All Three', hint: 'One letter · 3 bureau blocks' },
            { v: 'experian', l: 'Experian' },
            { v: 'equifax', l: 'Equifax' },
            { v: 'transunion', l: 'TransUnion' },
          ].map((b) => (
            <button
              key={b.v}
              onClick={() => setBureau(b.v)}
              className={`text-xs font-semibold py-2 rounded-lg border transition-colors ${
                bureau === b.v
                  ? (b.v === 'all'
                      ? 'bg-gradient-to-br from-amber-500 to-orange-600 border-amber-400 text-white'
                      : 'bg-fuchsia-600 border-fuchsia-500 text-white')
                  : 'bg-black/30 border-white/10 text-slate-300 hover:border-fuchsia-400/40'
              }`}
              data-testid={`dispute-bureau-${b.v}`}
              title={b.hint || ''}
            >
              {b.l}
            </button>
          ))}
        </div>
        {bureau === 'all' && (
          <div className="mt-2 text-[11px] text-amber-300">
            One PDF · all three bureau addresses stacked at the top of the letter.
          </div>
        )}
      </div>

      {/* Accounts */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
          Disputed Accounts <span className="text-slate-500 font-normal">(one per line)</span>
        </div>
        <div className="text-[11px] text-slate-500 mb-1.5">
          Format: <code className="text-fuchsia-300">Name | Category | Date | Balance</code>
          <br />
          Categories: <span className="text-slate-400">AUTO, CREDIT_CARD, PERSONAL_LOAN, STUDENT_LOAN, MORTGAGE, MEDICAL, UTILITY, COLLECTION, OTHER</span>
        </div>
        <textarea
          value={accountsInput}
          onChange={(e) => setAccountsInput(e.target.value)}
          rows={6}
          placeholder={"CAPITAL ONE | CREDIT_CARD | 07-2023 | $472\nCHASE AUTO | AUTO | 03-2020 | $12,500\nLVNV FUNDING | COLLECTION | 08-2025 | $1,001"}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 font-mono placeholder:text-slate-500 focus:border-fuchsia-400/60 focus:outline-none"
          data-testid="dispute-accounts-textarea"
        />
        <div className="text-[11px] text-slate-500 mt-1">
          {parsedAccounts.length} account{parsedAccounts.length === 1 ? '' : 's'} parsed. They&apos;ll be
          grouped by type in the final letter.
        </div>

        {/* Live preview — grouped by category, exact same layout the PDF
            will use.  Gives the user a "what will print" confirmation
            before they burn an LLM roundtrip on Step 3. */}
        {parsedAccountsGrouped.length > 0 && (
          <div
            className="mt-3 bg-black/50 border border-fuchsia-400/20 rounded-lg p-3 text-xs text-slate-200 space-y-3"
            data-testid="dispute-accounts-grouped-preview"
          >
            <div className="text-[10px] uppercase tracking-wider text-fuchsia-300 font-semibold">
              Preview — grouped exactly as the letter will render
            </div>
            {parsedAccountsGrouped.map((g) => (
              <div key={g.category}>
                {g.label && (
                  <div className="font-bold text-fuchsia-200 uppercase text-[11px] tracking-wider mb-1">
                    {g.label}
                  </div>
                )}
                <ul className="pl-4 space-y-0.5 list-disc list-outside">
                  {g.items.map((a, i) => (
                    <li key={i}>
                      {a.account_name}
                      {a.date_opened ? ` — ${a.date_opened}` : ''}
                      {a.balance ? ` — ${String(a.balance).startsWith('$') ? a.balance : '$' + a.balance}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* 2026-02-XX (user request "per-bureau inquiry disputes") —
            When the picked letter type is `inquiry_removal`, show a
            3-column bureau checkbox grid for each inquiry.  Default: all
            three bureaus checked (behaves like today's single combined
            letter).  Un-checking a bureau on any row triggers the
            per-bureau split: the Generate button produces ONE letter
            per bureau that still has at least one inquiry — each letter
            carrying ONLY that bureau's address block and ONLY the
            inquiries you ticked for it. */}
        {isInquiryMode() && parsedAccounts.length > 0 && (
          <div
            className="mt-4 rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-3"
            data-testid="dispute-inquiry-bureau-matrix"
          >
            <div className="text-[11px] uppercase tracking-wider text-cyan-300 font-bold mb-1">
              Which Bureau(s) Shows Each Inquiry?
            </div>
            <p className="text-[10px] text-cyan-200/70 mb-3">
              Tick which bureau(s) each inquiry lives on. The system makes ONE
              letter per bureau that still has at least one ticked — each
              letter carries only that bureau&apos;s address block.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-slate-200">
                <thead className="text-[10px] text-cyan-300/80 uppercase">
                  <tr>
                    <th className="text-left py-1 pr-2">Inquiry</th>
                    <th className="text-center px-2">Experian</th>
                    <th className="text-center px-2">Equifax</th>
                    <th className="text-center px-2">TransUnion</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedAccounts.map((a) => {
                    const aid = a.id || a.account_name;
                    const b = getInquiryBureaus(aid);
                    return (
                      <tr key={aid} className="border-t border-cyan-500/10">
                        <td className="py-1.5 pr-2">
                          <span className="font-semibold text-slate-100">{a.account_name}</span>
                          {a.date_opened && <span className="text-slate-400 ml-1">({a.date_opened})</span>}
                        </td>
                        {['experian', 'equifax', 'transunion'].map((bn) => (
                          <td key={bn} className="text-center px-2">
                            <input
                              type="checkbox"
                              checked={!!b[bn]}
                              onChange={(e) => setInquiryBureau(aid, bn, e.target.checked)}
                              className="accent-cyan-400 w-4 h-4"
                              data-testid={`inq-${aid.replace(/\s+/g, '_').toLowerCase()}-${bn}`}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(() => {
              // Compute default-vs-split state + which bureaus still have at least one inquiry.
              const matrixActive = parsedAccounts.some((a) => {
                const b = getInquiryBureaus(a.id || a.account_name);
                return !(b.experian && b.equifax && b.transunion);
              });
              const bureausWithInq = ['experian', 'equifax', 'transunion'].filter((bn) =>
                parsedAccounts.some((a) => getInquiryBureaus(a.id || a.account_name)[bn]),
              );
              if (bureausWithInq.length === 0) {
                return (
                  <div className="mt-2 text-[11px] text-rose-300">
                    No bureau has any inquiries checked. Tick at least one box or no letter will be generated.
                  </div>
                );
              }
              if (!matrixActive) {
                return (
                  <div className="mt-2 text-[11px] text-cyan-200/80">
                    → Will generate <b>1 combined letter</b> addressed to all 3 bureaus (default — un-tick any box to split into per-bureau letters).
                  </div>
                );
              }
              return (
                <div className="mt-2 text-[11px] text-cyan-200/80">
                  {bureausWithInq.length === 1
                    ? `→ Will generate 1 letter addressed to ${bureausWithInq[0]} only.`
                    : `→ Will generate ${bureausWithInq.length} separate letters — one per bureau with its own address block.`}
                </div>
              );
            })()}
          </div>
        )}

        {bankruptcyPicked && (
          <div
            className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3"
            data-testid="dispute-bankruptcy-fields"
          >
            <div className="text-[11px] uppercase tracking-wider text-amber-300 font-bold mb-1">
              Bankruptcy Case Details <span className="text-amber-200/60 normal-case tracking-normal">(optional — fill what you know)</span>
            </div>
            <p className="text-[10px] text-amber-200/70 mb-3">
              Anything you type here gets woven into the LLM-generated dispute so the letter cites the client&apos;s actual case, not a template.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-1 block">Chapter</label>
                <select
                  value={bankruptcyChapter}
                  onChange={(e) => setBankruptcyChapter(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-md bg-zinc-950 border border-amber-500/40 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="bk-chapter"
                >
                  <option value="">—</option>
                  <option value="7">Chapter 7</option>
                  <option value="11">Chapter 11</option>
                  <option value="13">Chapter 13</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-1 block">Filing Date</label>
                <input
                  type="date"
                  value={bankruptcyFilingDate}
                  onChange={(e) => setBankruptcyFilingDate(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-md bg-zinc-950 border border-amber-500/40 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="bk-filing-date"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-1 block">Court</label>
                <input
                  type="text"
                  value={bankruptcyCourt}
                  onChange={(e) => setBankruptcyCourt(e.target.value)}
                  placeholder="e.g. U.S. Bankruptcy Court, Northern District of California"
                  className="w-full px-2 py-1.5 rounded-md bg-zinc-950 border border-amber-500/40 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="bk-court"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-1 block">Case Number</label>
                <input
                  type="text"
                  value={bankruptcyCaseNumber}
                  onChange={(e) => setBankruptcyCaseNumber(e.target.value)}
                  placeholder="e.g. 24-12345"
                  className="w-full px-2 py-1.5 rounded-md bg-zinc-950 border border-amber-500/40 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="bk-case-number"
                />
              </div>
            </div>
          </div>
        )}

        {/* 2026-02-08 — Multi-letter mode: per-account letter checkboxes.
            Only rendered when the user picked 2+ letter types on Step 1.
            Each account can be assigned to any combination of the picked
            letters. Defaults to ALL letters checked so the wizard works
            out of the box; user unchecks the ones that don't apply. */}
        {isMulti && parsedAccounts.length > 0 && (
          <div
            className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3"
            data-testid="dispute-multi-assign-matrix"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-emerald-300 font-bold">
                Assign accounts to letters ({letterTypes.length} letters)
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    // Check every account on every letter (default state).
                    const next = {};
                    for (const a of parsedAccounts) {
                      next[a.id || a.account_name] = [...letterTypes];
                    }
                    setLetterAssignments(next);
                  }}
                  className="text-[10px] px-2 py-1 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 font-semibold"
                  data-testid="dispute-assign-all"
                >
                  All to all
                </button>
                <button
                  type="button"
                  onClick={() => setLetterAssignments({})}
                  className="text-[10px] px-2 py-1 rounded bg-zinc-700/40 hover:bg-zinc-700/60 text-zinc-300 font-semibold"
                  data-testid="dispute-assign-clear"
                >
                  Clear
                </button>
              </div>
            </div>
            <p className="text-[10px] text-emerald-200/70 mb-2">
              Tick which letters each account should appear on. An account can go on multiple.
              <br />
              <span className="text-fuchsia-300 font-semibold">
                Auto-assigned:
              </span>{' '}
              specialty letters (Timeshare, Bankruptcy, ID-Theft) only receive matching accounts by
              default — flip a box to override.
            </p>
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left text-[10px] font-bold text-emerald-300 uppercase tracking-wider pb-1 pr-2">Account</th>
                    {letterTypes.map((lt) => {
                      const meta = LETTER_TYPES.find((x) => x.value === lt);
                      return (
                        <th
                          key={lt}
                          className="px-2 pb-1 text-[10px] font-bold text-emerald-300 uppercase tracking-wider text-center"
                          title={meta?.label}
                        >
                          {meta?.label?.replace(' Letter', '').replace(' Dispute', '') || lt}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {parsedAccounts.map((a) => {
                    const aid = a.id || a.account_name;
                    // Default: every letter checked (no explicit assignment yet).
                    const assigned = letterAssignments[aid] || letterTypes;
                    return (
                      <tr key={aid} className="border-t border-emerald-500/15">
                        <td className="py-1.5 pr-2 text-slate-200 truncate max-w-[220px]" title={a.account_name}>
                          {a.account_name}
                          {a.balance ? <span className="text-slate-500 text-[10px] ml-1">${a.balance}</span> : null}
                        </td>
                        {letterTypes.map((lt) => {
                          const checked = assigned.includes(lt);
                          return (
                            <td key={lt} className="px-2 py-1.5 text-center">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setLetterAssignments((prev) => {
                                    const cur = prev[aid] || [...letterTypes];
                                    const next = cur.includes(lt)
                                      ? cur.filter((x) => x !== lt)
                                      : [...cur, lt];
                                    return { ...prev, [aid]: next };
                                  });
                                }}
                                className="w-4 h-4 accent-emerald-500 cursor-pointer"
                                data-testid={`dispute-assign-${aid}-${lt}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
      {/* 2026-02-08 — Multi-letter queue progress banner so the user
          knows which letter they're currently reviewing. */}
      {isMulti && letterQueue.length > 0 && (
        <div
          className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 flex items-center justify-between"
          data-testid="dispute-queue-progress"
        >
          <div className="text-xs text-emerald-200">
            <span className="font-bold uppercase tracking-wider mr-2">
              Letter {queueIndex + 1} of {letterQueue.length}
            </span>
            <span className="text-emerald-100/90">
              {LETTER_TYPES.find((x) => x.value === letterType)?.label || letterType}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {letterQueue.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full ${
                  i < queueIndex ? 'bg-emerald-500' : i === queueIndex ? 'bg-fuchsia-400' : 'bg-zinc-600'
                }`}
              />
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            {draftSource === 'llm' ? 'AI-generated unique wording' : 'Template default'}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Edit anything below. Accounts (grouped by type) are inserted between the two bodies.
          </div>
        </div>
        <button
          onClick={regenerateDraft}
          disabled={drafting}
          className={`${letterType === 'business' ? 'hidden' : 'inline-flex'} items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.10] disabled:opacity-50 text-slate-200 text-xs font-semibold px-3 py-1.5 rounded-lg border border-white/10 transition-colors`}
          data-testid="dispute-regenerate-btn"
        >
          {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Re-word
        </button>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1 block">
          Subject Line
        </label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-fuchsia-400/60 focus:outline-none"
          data-testid="dispute-subject-input"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1 block">
          Pre-Accounts Body <span className="text-slate-500 font-normal">(salutation + opening + legal basis)</span>
        </label>
        <textarea
          value={preBody}
          onChange={(e) => setPreBody(e.target.value)}
          rows={10}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-fuchsia-400/60 focus:outline-none whitespace-pre-wrap"
          data-testid="dispute-pre-body"
        />
      </div>

      {/* Accounts preview — grouped by type, showing EXACTLY how the
          final PDF will render them: bold uppercase category header +
          bold account name — date — $amount bullets.  Feb 2026 revamp
          per user request — this is the single source of truth for
          how accounts get laid out in the letter. */}
      {letterType === 'business' && (
        <p className="text-[11px] text-slate-400" data-testid="business-accounts-note">Business letter: the accounts and their dispute reasons are written in the first body above — edit them there.</p>
      )}
      <div className={letterType === 'business' ? 'hidden' : ''}>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1 flex items-center gap-2">
          <span>Accounts (grouped, inserted here in the PDF)</span>
          <span className="text-fuchsia-400/70 normal-case tracking-normal">— live preview of PDF layout</span>
        </div>

        {/* ─── Bulk-assign toolbar ─────────────────────────────
            Nov 2026 — user asked for a way to grab multiple accounts at
            once and drop them under one category (e.g. 9 credit cards)
            without clicking each row's dropdown one-by-one. */}
        {groupedAccounts.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md bg-fuchsia-500/10 border border-fuchsia-500/30 px-2 py-1.5"
            data-testid="bulk-assign-bar">
            <button
              type="button"
              onClick={selectAllVisible}
              className="text-[11px] px-2 py-1 rounded-md bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-bold"
              data-testid="bulk-select-all"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={clearSelection}
              disabled={selectedKeys.size === 0}
              className={`text-[11px] px-2 py-1 rounded-md font-bold ${
                selectedKeys.size === 0
                  ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                  : 'bg-zinc-700 hover:bg-zinc-600 text-white'
              }`}
              data-testid="bulk-clear"
            >
              Clear
            </button>
            <span className="text-[11px] font-bold text-fuchsia-200">
              {selectedKeys.size} selected
            </span>
            <div className="ml-auto flex items-center gap-1.5">
              <span className="text-[11px] text-zinc-300">Move to →</span>
              <select
                value={bulkTargetCategory}
                onChange={(e) => setBulkTargetCategory(e.target.value)}
                className="text-[11px] px-2 py-1 rounded-md border border-fuchsia-400/60 bg-white text-slate-800 font-semibold"
                data-testid="bulk-target-select"
              >
                {CATEGORY_ORDER.map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c] || 'Other'}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => bulkMoveSelected(bulkTargetCategory)}
                disabled={selectedKeys.size === 0}
                className={`text-[11px] px-2 py-1 rounded-md font-bold ${
                  selectedKeys.size === 0
                    ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                }`}
                data-testid="bulk-apply"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        <div
          className="bg-white/95 border border-fuchsia-400/30 rounded-lg p-4 text-slate-900 space-y-3 shadow-inner"
          data-testid="accounts-grouped-live-preview"
          style={{ fontFamily: '"Georgia", "Times New Roman", serif' }}
        >
          {groupedAccounts.length === 0 && (
            <div className="text-slate-500 italic text-xs">No accounts parsed.</div>
          )}
          {groupedAccounts.map((g, gIdx) => {
            // Mirror backend _strip_category_from_name — strip the category
            // word from the start or end of the account name so it isn't
            // duplicated under its own header.
            const stripCat = (name, label) => {
              if (!name || !label || label.toUpperCase() === "OTHER") return name || "";
              const n = String(name).trim();
              const esc = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
              const suffix = new RegExp(`[\\s\\-\u2013\u2014,:/]+${esc}\\b\\.?$`, "i");
              let m = n.match(suffix);
              if (m) {
                const s = n.substring(0, m.index).replace(/[\s\t\-,:/\u2013\u2014]+$/, "");
                if (s) return s;
              }
              const prefix = new RegExp(`^${esc}[\\s\\-\u2013\u2014,:/]+`, "i");
              m = n.match(prefix);
              if (m) {
                const s = n.substring(m[0].length).replace(/^[\s\t\-,:/\u2013\u2014]+/, "");
                if (s) return s;
              }
              return n;
            };
            return (
              <div key={`${g.category}-${gIdx}`}>
                {g.label && (
                  <div className="font-black text-slate-900 uppercase text-[13px] tracking-wider underline mb-1">
                    {g.label.toUpperCase()}
                  </div>
                )}
                <div className="pl-1 space-y-1">
                  {g.items.map((a, i) => {
                    const displayName = stripCat(a.account_name, g.label || "");
                    return (
                      <div key={i} className="flex items-center gap-2 group" data-testid={`grouped-row-${g.category}-${i}`}>
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(`${g.category}:${i}`)}
                          onChange={() => toggleSelected(`${g.category}:${i}`)}
                          className="w-4 h-4 accent-fuchsia-500 cursor-pointer"
                          data-testid={`grouped-row-check-${g.category}-${i}`}
                          aria-label={`Select ${a.account_name}`}
                        />
                        {/* 2026-02-05 (user request) — inline editable fields.
                            Name is the display name (category-stripped) so
                            the user sees the same thing that renders in the
                            PDF; on change we write it back to the raw
                            account_name field. */}
                        <input
                          type="text"
                          value={displayName}
                          onChange={(e) => updateAccountField(g.category, i, 'account_name', e.target.value)}
                          placeholder="Creditor name"
                          className="flex-1 min-w-0 text-[13px] leading-snug bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-900 focus:border-fuchsia-500 focus:outline-none"
                          data-testid={`grouped-row-name-${g.category}-${i}`}
                        />
                        <input
                          type="text"
                          value={a.date_opened || ''}
                          onChange={(e) => updateAccountField(g.category, i, 'date_opened', e.target.value)}
                          placeholder="MM-YYYY"
                          className="w-[88px] text-[12px] leading-snug bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-900 focus:border-fuchsia-500 focus:outline-none"
                          data-testid={`grouped-row-date-${g.category}-${i}`}
                        />
                        <input
                          type="text"
                          value={a.balance ? String(a.balance).replace(/^\$/, '') : ''}
                          onChange={(e) => updateAccountField(g.category, i, 'balance', e.target.value.replace(/^\$/, ''))}
                          placeholder="$0"
                          className="w-[76px] text-[12px] leading-snug bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-900 focus:border-fuchsia-500 focus:outline-none text-right"
                          data-testid={`grouped-row-balance-${g.category}-${i}`}
                        />
                        {/* Inline category-move dropdown — the user's
                            single-source-of-truth to fix any misplaced
                            account before the PDF is generated. */}
                        <select
                          value={g.category}
                          onChange={(e) => moveAccountToCategory(g.category, i, e.target.value)}
                          className="text-[11px] px-2 py-1 rounded-md border border-fuchsia-400/50 bg-white text-slate-800 hover:border-fuchsia-500 opacity-70 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                          data-testid={`grouped-row-cat-${g.category}-${i}`}
                          title="Move this account to a different category"
                        >
                          {CATEGORY_ORDER.map((c) => (
                            <option key={c} value={c}>
                              {(CATEGORY_LABELS[c] || "Other")}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeAccountFromGroup(g.category, i)}
                          className="h-7 w-7 flex items-center justify-center rounded-md bg-red-500/10 hover:bg-red-500/25 border border-red-400/40 text-red-500 opacity-70 group-hover:opacity-100 transition"
                          title="Remove this account from the letter"
                          data-testid={`grouped-row-delete-${g.category}-${i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  {/* Add-account button per category (inline row) */}
                  <button
                    type="button"
                    onClick={() => addAccountToGroup(g.category)}
                    className="mt-1 text-[11px] font-bold uppercase tracking-wider text-fuchsia-600 hover:text-fuchsia-800 flex items-center gap-1"
                    data-testid={`add-account-${g.category}`}
                  >
                    + Add account to {(g.label || 'Other').toUpperCase()}
                  </button>
                </div>
              </div>
            );
          })}
          {/* Master "add another category" so the user can introduce a
              category that doesn't exist yet (e.g. add a MORTGAGE row
              when the letter only had CREDIT_CARDs). */}
          <div className="pt-2 mt-2 border-t border-fuchsia-500/25 flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
              Add category:
            </span>
            <select
              value=""
              onChange={(e) => { if (e.target.value) { addNewCategoryGroup(e.target.value); e.target.value = ''; } }}
              className="text-[11px] px-2 py-1 rounded-md border border-fuchsia-400/50 bg-white text-slate-800"
              data-testid="add-new-category-select"
            >
              <option value="" disabled hidden>Pick a category…</option>
              {CATEGORY_ORDER.filter((c) => !groupedAccounts.some((g) => g.category === c)).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] || 'Other'}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sworn Statement Cover Page — Feb 2026: this is now controlled
          by an explicit Yes / Skip prompt that fires when the user
          clicks "Generate PDF" on Step 4, per user request.  No
          checkbox here anymore. */}

      <div>
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1 block">
          Post-Accounts Body <span className="text-slate-500 font-normal">(demand + escalation + attachments + closing)</span>
        </label>
        <textarea
          value={postBody}
          onChange={(e) => setPostBody(e.target.value)}
          rows={12}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-fuchsia-400/60 focus:outline-none whitespace-pre-wrap"
          data-testid="dispute-post-body"
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
      {customLetters.length > 0 && (
        <div className="rounded-xl border border-violet-500/40 bg-violet-500/5 p-3" data-testid="dispute-custom-letters">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] uppercase tracking-wider text-violet-300 font-bold">
              Custom AI letters ({customLetters.length}) — downloaded with this batch
            </div>
            <button type="button" onClick={downloadCustomLetters} data-testid="dispute-custom-letters-download"
              className="text-[11px] font-bold text-violet-200 border border-violet-400/50 rounded-md px-2 py-1 hover:bg-violet-500/20">
              Download now
            </button>
          </div>
          <ul className="mt-1.5 space-y-0.5">
            {customLetters.map((c) => (
              <li key={c.account_name} className="text-xs text-zinc-300" data-testid={`dispute-custom-letter-${normName(c.account_name)}`}>
                {c.account_name}{c.bureau ? ` · ${c.bureau}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* 2026-02-28 (user request) — Per-Bureau Account Routing.
          Only shown when the admin picked "All Bureaus" on Step 1.
          Admin unchecks which bureaus DON'T report each account, picks
          whether to generate one combined letter or three separate
          letters, and the Generate button honours the plan.  An all-TU
          client only sees the TU address at the top of the letter. */}
      {bureau === 'all' && parsedAccounts.length > 0 && (
        <div
          className="rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-3"
          data-testid="dispute-bureau-routing"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-wider text-cyan-300 font-bold">
              Per-Bureau Account Routing
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  // All accounts tagged for all 3 bureaus (default).
                  const next = {};
                  for (const a of parsedAccounts) {
                    next[a.id || a.account_name] = ['experian', 'equifax', 'transunion'];
                  }
                  setAccountBureaus(next);
                }}
                className="text-[10px] px-2 py-1 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 font-semibold"
                data-testid="dispute-bureau-all"
              >
                All on all 3
              </button>
              <div
                className="inline-flex items-center gap-0 rounded-md bg-black/40 p-0.5 ml-1 border border-white/10"
                data-testid="dispute-bureau-mode-toggle"
              >
                <button
                  type="button"
                  onClick={() => setMultiBureauMode('combined')}
                  className={`text-[10px] px-2 py-1 rounded transition-colors ${
                    multiBureauMode === 'combined'
                      ? 'bg-cyan-500 text-black font-semibold'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  data-testid="dispute-bureau-mode-combined"
                >
                  🅰️ Combined (1 letter)
                </button>
                <button
                  type="button"
                  onClick={() => setMultiBureauMode('separate')}
                  className={`text-[10px] px-2 py-1 rounded transition-colors ${
                    multiBureauMode === 'separate'
                      ? 'bg-cyan-500 text-black font-semibold'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  data-testid="dispute-bureau-mode-separate"
                >
                  🅱️ Separate (up to 3 letters)
                </button>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-cyan-200/70 mb-2">
            Tick which bureaus report each account. In <span className="font-semibold">Combined</span>{' '}
            mode we hide the headers for bureaus with no tagged accounts.  In{' '}
            <span className="font-semibold">Separate</span> mode we generate one letter per bureau,
            each filtered to its own accounts — downloaded one after the other.
          </p>
          <div className="overflow-x-auto -mx-1 px-1">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left text-[10px] font-bold text-cyan-300 uppercase tracking-wider pb-1 pr-2">
                    Account
                  </th>
                  {['experian', 'equifax', 'transunion'].map((b) => (
                    <th
                      key={b}
                      className="px-2 pb-1 text-[10px] font-bold text-cyan-300 uppercase tracking-wider text-center"
                    >
                      {b === 'experian' ? 'EXP' : b === 'equifax' ? 'EQ' : 'TU'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsedAccounts.map((a) => {
                  const aid = a.id || a.account_name;
                  const tagged = accountBureaus[aid] || ['experian', 'equifax', 'transunion'];
                  return (
                    <tr key={aid} className="border-t border-cyan-500/15">
                      <td className="py-1.5 pr-2 text-slate-200 truncate max-w-[260px]" title={a.account_name}>
                        {a.account_name}
                        {a.balance ? (
                          <span className="text-slate-500 text-[10px] ml-1">${a.balance}</span>
                        ) : null}
                      </td>
                      {['experian', 'equifax', 'transunion'].map((b) => {
                        const on = tagged.includes(b);
                        return (
                          <td key={b} className="px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => {
                                setAccountBureaus((prev) => {
                                  const cur = prev[aid] || ['experian', 'equifax', 'transunion'];
                                  const next = cur.includes(b)
                                    ? cur.filter((x) => x !== b)
                                    : [...cur, b];
                                  return { ...prev, [aid]: next };
                                });
                              }}
                              className="w-4 h-4 accent-cyan-500 cursor-pointer"
                              data-testid={`dispute-bureau-chip-${aid}-${b}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Live plan summary — show what will actually be downloaded */}
          {(() => {
            const _all = ['experian', 'equifax', 'transunion'];
            const _bFor = (aid) => accountBureaus[aid] || _all;
            if (multiBureauMode === 'separate') {
              const plan = _all.map((b) => ({
                bureau: b,
                count: parsedAccounts.filter((a) => _bFor(a.id || a.account_name).includes(b)).length,
              })).filter((x) => x.count > 0);
              return (
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                  <span className="text-cyan-200/80">Will download:</span>
                  {plan.length === 0 ? (
                    <span className="text-red-300">nothing yet — tick at least one bureau</span>
                  ) : plan.map((p) => (
                    <span key={p.bureau} className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-100">
                      {p.bureau[0].toUpperCase() + p.bureau.slice(1)} letter · {p.count} account{p.count === 1 ? '' : 's'}
                    </span>
                  ))}
                </div>
              );
            }
            const tagged = new Set();
            for (const a of parsedAccounts) {
              for (const b of _bFor(a.id || a.account_name)) tagged.add(b);
            }
            const headers = _all.filter((b) => tagged.has(b));
            return (
              <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                <span className="text-cyan-200/80">Will download:</span>
                <span className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-100">
                  1 letter · headers for {headers.length === 0 ? 'no bureaus (pick at least one)' : headers.map((b) => b[0].toUpperCase() + b.slice(1)).join(' + ')}
                </span>
              </div>
            );
          })()}
        </div>
      )}

      {/* Signature format — 3-way: typed / handwritten / BOTH */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
          Letter Format
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => setStyle('typed')}
            className={`flex flex-col items-center justify-center gap-1 text-sm font-semibold py-3 rounded-lg border transition-colors ${
              style === 'typed'
                ? 'bg-fuchsia-600 border-fuchsia-500 text-white'
                : 'bg-black/30 border-white/10 text-slate-300 hover:border-fuchsia-400/40'
            }`}
            data-testid="dispute-style-typed"
          >
            <Keyboard className="w-4 h-4" />
            <span>Computer / Typed</span>
          </button>
          <button
            onClick={() => setStyle('handwritten')}
            className={`flex flex-col items-center justify-center gap-1 text-sm font-semibold py-3 rounded-lg border transition-colors ${
              style === 'handwritten'
                ? 'bg-fuchsia-600 border-fuchsia-500 text-white'
                : 'bg-black/30 border-white/10 text-slate-300 hover:border-fuchsia-400/40'
            }`}
            data-testid="dispute-style-handwritten"
          >
            <PenLine className="w-4 h-4" />
            <span>Handwritten</span>
          </button>
          <button
            onClick={() => setStyle('both')}
            className={`flex flex-col items-center justify-center gap-1 text-sm font-semibold py-3 rounded-lg border transition-colors ${
              style === 'both'
                ? 'bg-gradient-to-br from-amber-500 to-orange-600 border-amber-400 text-white'
                : 'bg-black/30 border-white/10 text-slate-300 hover:border-amber-400/40'
            }`}
            data-testid="dispute-style-both"
          >
            <FileText className="w-4 h-4" />
            <span>Both</span>
          </button>
        </div>
        {style === 'both' && (
          <div className="mt-2 text-[11px] text-amber-300">
            Two PDFs will download — one typed, one handwritten.
          </div>
        )}
      </div>

      {/* Cursive signature font — visual previews (shown for typed, both,
          and handwritten because a cursive sig is drawn on every letter). */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
          Signature Font <span className="text-slate-500 font-normal">(cursive line at the bottom)</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SIG_FONTS.map((f) => (
            <button
              key={f.value}
              onClick={() => setSignatureFont(f.value)}
              className={`text-left px-3 py-3 rounded-xl border transition-all ${
                signatureFont === f.value
                  ? 'bg-fuchsia-600/15 border-fuchsia-400/60 ring-1 ring-fuchsia-400/30'
                  : 'bg-black/30 border-white/10 hover:border-fuchsia-400/30'
              }`}
              data-testid={`dispute-signature-font-${f.value}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                {f.label}
              </div>
              <div
                className="mt-1 text-2xl leading-tight text-white truncate"
                style={{ fontFamily: f.preview }}
                data-testid={`dispute-signature-preview-${f.value}`}
              >
                {/* Feb 2026 — title-case the client name in the preview
                    so it renders as "Joseph Hodges Jr" instead of the
                    all-caps version the user might have typed. Mirrors
                    the backend _titlecase_signature() logic. */}
                {(() => {
                  const name = (clientName || 'Your Name').trim() || 'Your Name';
                  return name.split(/\s+/).map((p) => (
                    p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : p
                  )).join(' ');
                })()}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">{f.subLabel}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Typed body font — visual preview cards, only relevant for typed / both. */}
      {(style === 'typed' || style === 'both') && (
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
            Typed Body Font <span className="text-slate-500 font-normal">(what the printed letter body looks like)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {BODY_FONTS.map((f) => (
              <button
                key={f.value}
                onClick={() => setBodyFont(f.value)}
                className={`text-left px-3 py-3 rounded-xl border transition-all ${
                  bodyFont === f.value
                    ? 'bg-fuchsia-600/15 border-fuchsia-400/60 ring-1 ring-fuchsia-400/30'
                    : 'bg-black/30 border-white/10 hover:border-fuchsia-400/30'
                }`}
                data-testid={`dispute-body-font-${f.value}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                  {f.label}
                </div>
                <div
                  className="mt-1 text-lg leading-snug text-white truncate"
                  style={{ fontFamily: f.preview }}
                >
                  The quick brown fox
                </div>
                <div className="text-[10px] text-slate-500 mt-1">{f.subLabel}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Handwriting body style — visual preview cards (same UX as the
          Handwritten Letter Converter). Shown for handwritten OR both. */}
      {(style === 'handwritten' || style === 'both') && (
        <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-3">
          {/* Original vs Copy — controls how the finished PDF looks:
              a fresh pen letter vs a photocopy of one. */}
          <div className="text-xs uppercase tracking-wider text-amber-300 font-semibold mb-2 flex items-center gap-1.5">
            <PenLine className="w-3.5 h-3.5" />
            Letter Finish
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              onClick={() => setHwFinish('original')}
              className={`text-left px-3 py-3 rounded-xl border transition-all ${
                hwFinish === 'original'
                  ? 'bg-amber-500/20 border-amber-400/70 ring-1 ring-amber-400/40'
                  : 'bg-black/30 border-white/10 hover:border-amber-400/40'
              }`}
              data-testid="dispute-hw-finish-original"
            >
              <div className="text-sm font-semibold text-white">Original pen letter</div>
              <div className="text-[10px] uppercase tracking-wider text-amber-200/80 mt-1 font-semibold">
                Clean · straight · fresh ink
              </div>
            </button>
            <button
              onClick={() => setHwFinish('copied')}
              className={`text-left px-3 py-3 rounded-xl border transition-all ${
                hwFinish === 'copied'
                  ? 'bg-amber-500/20 border-amber-400/70 ring-1 ring-amber-400/40'
                  : 'bg-black/30 border-white/10 hover:border-amber-400/40'
              }`}
              data-testid="dispute-hw-finish-copied"
            >
              <div className="text-sm font-semibold text-white">Copy of an original pen letter</div>
              <div className="text-[10px] uppercase tracking-wider text-amber-200/80 mt-1 font-semibold">
                Slight rotation · toner ink · dust specks
              </div>
            </button>
          </div>

          <div className="text-xs uppercase tracking-wider text-amber-300 font-semibold mb-2 flex items-center gap-1.5">
            <PenLine className="w-3.5 h-3.5" />
            Handwriting Style
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="dispute-hw-style-grid">
            {HW_STYLES.map((s) => (
              <button
                key={s.value}
                onClick={() => setHwStyle(s.value)}
                className={`text-left px-3 py-3 rounded-xl border transition-all ${
                  hwStyle === s.value
                    ? 'bg-amber-500/20 border-amber-400/70 ring-1 ring-amber-400/40'
                    : 'bg-black/30 border-white/10 hover:border-amber-400/40'
                }`}
                data-testid={`dispute-hw-style-${s.value}`}
              >
                <div
                  className="text-xl leading-tight text-white"
                  style={{ fontFamily: s.preview }}
                >
                  {s.label}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-amber-200/80 mt-1 font-semibold">
                  {s.subLabel}
                </div>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-amber-200/80 mt-2">
            ⏳ Handwriting conversion adds ~15–30 seconds per letter.
          </p>
        </div>
      )}

      {/* Recap */}
      <div className="bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-slate-300 space-y-1">
        <div><span className="text-slate-500">Type:</span> {letterType === 'second_round' ? 'Second Round Verified Account' : letterType === 'inquiry_removal' ? 'Inquiry Removal (FCRA §604 & §616)' : letterType === 'chexsystems' ? 'ChexSystems Identity Theft Dispute' : letterType === 'business' ? 'Business Account Removal (Equifax · Experian · D&B)' : letterType === 'bankruptcy' ? 'Bankruptcy Dispute (§1681e(b) & §1681i)' : letterType === 'late_payments' ? 'Late Payments Dispute (FCBA §1666)' : letterType === 'child_support' ? 'Child Support Dispute (FCRA §1681b / §1681i)' : 'Regular Dispute'}</div>
        <div><span className="text-slate-500">Bureau:</span> {bureau}</div>
        <div><span className="text-slate-500">Accounts:</span> {parsedAccounts.length}</div>
        <div><span className="text-slate-500">Format:</span> {style === 'both' ? 'Typed + Handwritten (2 PDFs)' : (style === 'handwritten' ? 'Handwritten' : 'Typed')}</div>
        <div><span className="text-slate-500">Wording:</span> {draftSource === 'llm' ? 'AI-uniquely-worded' : draftSource === 'saved' ? 'From saved letter' : 'template'}</div>
      </div>

      {/* Feb 2026 — Letter Date picker (optional).  Empty = today (PT).
          Used so re-rendered saved letters can carry a specific date. */}
      <div className="bg-black/30 border border-white/10 rounded-lg p-3">
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between mb-2">
          <span>Letter Date</span>
          {letterDate && (
            <button
              onClick={() => setLetterDate('')}
              className="text-[10px] text-fuchsia-300 hover:text-fuchsia-200 uppercase tracking-wider"
              data-testid="dispute-letter-date-clear"
            >
              Clear
            </button>
          )}
        </label>
        <input
          type="date"
          value={letterDate}
          onChange={(e) => setLetterDate(e.target.value)}
          className="w-full bg-black/60 border border-white/10 focus:border-fuchsia-400/60 rounded-lg px-3 py-2 text-sm text-white outline-none"
          data-testid="dispute-letter-date-input"
        />
        <p className="text-[10px] text-slate-500 mt-1.5">
          Leave blank to use today&apos;s date (Pacific time).
        </p>
      </div>
    </div>
  );

  const renderFooter = () => (
    <div className="flex gap-2 p-5 border-t border-white/10">
      {step > 1 ? (
        <button
          onClick={() => setStep(step - 1)}
          disabled={drafting || generating}
          className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.10] disabled:opacity-50 text-slate-200 text-sm font-medium py-2.5 px-4 rounded-xl transition-colors"
          data-testid="dispute-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      ) : (
        <button
          onClick={close}
          className="bg-white/[0.06] hover:bg-white/[0.10] text-slate-200 text-sm font-medium py-2.5 px-4 rounded-xl transition-colors"
        >
          Cancel
        </button>
      )}
      <div className="flex-1" />
      {step === 1 && (
        <button
          onClick={() => setStep(2)}
          className="inline-flex items-center gap-1.5 bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white text-sm font-semibold py-2.5 px-5 rounded-xl shadow-md shadow-fuchsia-900/40 transition-colors"
          data-testid="dispute-next-1"
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      )}
      {step === 2 && (
        <button
          onClick={() => {
            // 2026-09-25 — a fresh generation run resets the cached
            // sworn-statement choice so we prompt exactly once per run.
            swornChoiceForQueueRef.current = null;

            // 2026-02-XX (user request) — Per-bureau inquiry expansion.
            // If `inquiry_removal` is picked AND the user has at least
            // ONE box un-ticked in the bureau matrix (meaning they want
            // the per-bureau split), expand the single letter type into
            // one queue entry per bureau that still has inquiries, and
            // ride the existing multi-letter queue machinery.  Each
            // entry is an OBJECT (not a string) so downstream can tell
            // which per-bureau subset to filter + address.
            const buildInquirySplit = () => {
              const matrixActive = parsedAccounts.some((a) => {
                const aid = a.id || a.account_name;
                const b = getInquiryBureaus(aid);
                return !(b.experian && b.equifax && b.transunion);
              });
              if (!matrixActive) return null; // user kept defaults → single combined letter
              const bureausWithInq = ['experian', 'equifax', 'transunion'].filter((bn) =>
                parsedAccounts.some((a) => getInquiryBureaus(a.id || a.account_name)[bn]),
              );
              if (bureausWithInq.length === 0) {
                toast.error('No bureau has any inquiries ticked — tick at least one box.');
                return [];
              }
              return bureausWithInq.map((bn) => ({
                letterType: 'inquiry_removal',
                bureau: bn,
                accountIds: parsedAccounts
                  .filter((a) => getInquiryBureaus(a.id || a.account_name)[bn])
                  .map((a) => a.id || a.account_name),
              }));
            };

            // Case A — ONLY inquiry_removal is picked + user triggered split.
            if (letterTypes.length === 1 && letterTypes[0] === 'inquiry_removal') {
              const split = buildInquirySplit();
              if (split && split.length === 0) return; // error already shown
              if (split) {
                setLetterQueue(split);
                setQueueIndex(0);
                setLetterType('inquiry_removal');
                setBureau(split[0].bureau);
                setTimeout(() => generateDraft('inquiry_removal', split[0]), 20);
                return;
              }
              // No split — fall through to single-letter path.
            }

            // 2026-02-08 multi-letter mode — build the queue from the
            // types the user actually assigned accounts to. Single-letter
            // mode short-circuits with a queue of one.
            if (isMulti) {
              const queue = letterTypes.filter((lt) => {
                return parsedAccounts.some((a) => {
                  const aid = a.id || a.account_name;
                  const assigned = letterAssignments[aid] || letterTypes;
                  return assigned.includes(lt);
                });
              });
              if (queue.length === 0) {
                toast.error('Assign at least one account to a letter first');
                return;
              }
              // 2026-02-XX — If `inquiry_removal` is one of the types AND
              // the per-bureau matrix is active, splice its single queue
              // slot into N per-bureau slots.
              const inquirySplit = queue.includes('inquiry_removal') ? buildInquirySplit() : null;
              let finalQueue;
              if (inquirySplit && inquirySplit.length > 0) {
                finalQueue = queue.flatMap((lt) => lt === 'inquiry_removal' ? inquirySplit : [lt]);
              } else {
                finalQueue = queue;
              }
              setLetterQueue(finalQueue);
              setQueueIndex(0);
              const first = finalQueue[0];
              const firstType = typeof first === 'string' ? first : first.letterType;
              setLetterType(firstType);
              if (typeof first !== 'string' && first.bureau) setBureau(first.bureau);
              setTimeout(() => generateDraft(firstType, typeof first === 'string' ? null : first), 20);
            } else {
              setLetterQueue([]);
              setQueueIndex(0);
              generateDraft();
            }
          }}
          disabled={drafting || parsedAccounts.length === 0}
          className="inline-flex items-center gap-1.5 bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 disabled:opacity-50 text-white text-sm font-semibold py-2.5 px-5 rounded-xl shadow-md shadow-fuchsia-900/40 transition-colors"
          data-testid="dispute-draft-btn"
        >
          {drafting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Drafting unique letter…</>
          ) : isMulti ? (
            <><Sparkles className="w-4 h-4" /> Generate {letterTypes.length} Letters</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Generate Draft</>
          )}
        </button>
      )}
      {step === 3 && (
        <button
          onClick={() => setStep(4)}
          className="inline-flex items-center gap-1.5 bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white text-sm font-semibold py-2.5 px-5 rounded-xl shadow-md shadow-fuchsia-900/40 transition-colors"
          data-testid="dispute-next-3"
        >
          Looks Good — Continue <ArrowRight className="w-4 h-4" />
        </button>
      )}
      {step === 4 && (
        <button
          onClick={() => {
            // 2026-09-25 — In multi-letter mode, ask the sworn-statement
            // question ONLY on the first letter of the queue.  For every
            // subsequent letter, re-use the cached choice and skip the
            // prompt.  This matches the user's request: "no matter how
            // many dispute letters you choose, prompt for the sworn
            // statement one time".
            if (isMulti && swornChoiceForQueueRef.current !== null) {
              generatePDF(swornChoiceForQueueRef.current);
              return;
            }
            setShowSwornPrompt(true);
          }}
          disabled={generating}
          className="inline-flex items-center gap-1.5 bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 disabled:opacity-50 text-white text-sm font-semibold py-2.5 px-5 rounded-xl shadow-md shadow-fuchsia-900/40 transition-colors"
          data-testid="dispute-generate-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
          ) : (
            <><Download className="w-4 h-4" /> {style === 'both' ? 'Generate & Download Both PDFs' : 'Generate & Download PDF'}</>
          )}
        </button>
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={close}
      data-testid="dispute-letter-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-gradient-to-br from-slate-900 to-zinc-900 border border-fuchsia-500/30 rounded-2xl w-full max-w-3xl my-6 shadow-2xl"
        data-testid="dispute-letter-modal"
      >
        {renderHeader()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {renderFooter()}

        {/* Feb 2026 — Sworn Statement Yes/Skip prompt.  Fires when the
            user clicks "Generate PDF" so they can decide per-letter
            whether the client needs the sworn statement page. */}
        {showSwornPrompt && (
          <div
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowSwornPrompt(false)}
            data-testid="dispute-sworn-prompt-overlay"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-900 to-zinc-900 border border-emerald-500/40 rounded-2xl w-full max-w-md p-6 shadow-2xl shadow-emerald-900/40"
              data-testid="dispute-sworn-prompt-modal"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-emerald-300" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">
                    Include a Sworn Statement?
                  </h3>
                  <p className="text-sm text-slate-300 leading-relaxed mt-1">
                    Do you need a{" "}
                    <span className="font-semibold text-emerald-200">
                      Sworn Statement of Personal Submission
                    </span>{" "}
                    for <span className="text-white">{clientName || 'the client'}</span>?
                    It&apos;s a separate one-page PDF affirming under penalty of
                    perjury that the client personally submitted the packet
                    (not a third-party company).  Some client scenarios
                    don&apos;t need it.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setIncludeSwornStatement(false);
                    setShowSwornPrompt(false);
                    // Cache for the rest of the multi-letter queue so
                    // subsequent letters don't re-prompt.
                    swornChoiceForQueueRef.current = false;
                    generatePDF(false);
                  }}
                  className="flex-1 bg-white/[0.06] hover:bg-white/[0.10] text-slate-200 text-sm font-medium py-2.5 rounded-xl transition-colors"
                  data-testid="dispute-sworn-prompt-skip"
                >
                  Skip
                </button>
                <button
                  onClick={() => {
                    setIncludeSwornStatement(true);
                    setShowSwornPrompt(false);
                    swornChoiceForQueueRef.current = true;
                    generatePDF(true);
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-br from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-sm font-semibold py-2.5 rounded-xl shadow-md shadow-emerald-900/40 transition-colors"
                  data-testid="dispute-sworn-prompt-yes"
                >
                  <Download className="w-4 h-4" /> Yes, download it
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 2026-02-28 (user request) — Structured aggressive Late-Payments
            dispute entry.  Pops when the user checks the "Late Payments"
            tile.  On generate, the returned PDF blob is auto-downloaded
            and stashed so the parent Finalize flow can re-download it. */}
        <BusinessInfoDialog open={showBusinessInfo} value={businessInfo} onSave={setBusinessInfo} onClose={() => setShowBusinessInfo(false)} />
        <LatePaymentsDisputeModal
          open={showLatePaymentsModal}
          onClose={() => setShowLatePaymentsModal(false)}
          clientData={{
            name: clientName || '',
            address: (clientAddressLines && clientAddressLines[0]) || '',
            // Best-effort city/state/zip parse from the 2nd address line
            // ("City, ST 12345") the parent typically supplies.
            ...(() => {
              const line2 = (clientAddressLines && clientAddressLines[1]) || '';
              const m = line2.match(/^(.*?),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)/);
              return m ? { city: m[1], state: m[2], zip_code: m[3] } : {};
            })(),
          }}
          signatureData={null}
          // 2026-02-28 (user request) — Late-payment letter must flow through
          // the SAME signature/font/handwriting pipeline every other dispute
          // letter uses.  Pipe the parent's live choices in so the child
          // modal has no duplicate picker.
          style={style === 'both' ? 'handwritten' : style}
          signatureFont={signatureFont}
          bodyFont={bodyFont}
          handwritingStyle={hwStyle}
          handwritingFinish={hwFinish}
          onGenerated={(blob, filename /* , entry */) => {
            setLatePaymentsBlob(blob);
            setLatePaymentsFilename(filename);
            // Trigger an immediate download so the admin gets the PDF
            // right away (same UX as the other letter types).
            try {
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              // Revoke after a beat so the click has already latched.
              setTimeout(() => URL.revokeObjectURL(url), 2000);
            } catch (e) {
              console.error('[LATE-PMT] auto-download failed:', e);
            }
          }}
        />
      </div>
    </div>
  );
}
